import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import { 
  formatToDateString, 
  isValidDateRange, 
  preparePeriodForApi, 
  debugDates,
  convertMuiDate 
} from '../../utils/dateUtils';
import { getErrorMessage } from '../../utils/listingErrorHandler';
import {
  Box,
  Button,
  Typography,
  TextField,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Autocomplete,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tab,
  Tabs,
  FormHelperText,
  LinearProgress
} from '@mui/material';
import ListingMap from './ListingMap';
import RideShareMap from './RideShareMap';
import BookingSettingsStep from './BookingSettingsStep';
import geocodingService from '../../services/geocodingService';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  InsertDriveFile as InsertDriveFileIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  MyLocation,
  Today as TodayIcon,
  EventAvailable as EventAvailableIcon,
  CalendarMonth as CalendarMonthIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
  LockOpen as FreeIcon,
  ArrowBack as ArrowBackIcon,
  AutoFixHigh as AutoFixHighIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCategories } from '../../context/CategoryContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useCategoryDescription, useCategoryName } from '../../utils/categoryTranslation';
import { getCurrencyByCountry } from '../../config/currencies';
import { getCurrencyForCountry, getCurrencySymbol } from '../../utils/currencyUtils';
import api from '../../services/api';

// Import des composants de disponibilité
import NewAvailabilityManager from '../availability/NewAvailabilityManager';
import PeriodFormModal from '../availability/PeriodFormModal';
import { 
  fetchAvailability, 
  addAvailabilityPeriod, 
  updateAvailabilityPeriod, 
  deleteAvailabilityPeriod,
  deleteAvailabilityPeriodViaListing 
} from '../../services/availabilityService';
import { AVAILABILITY_STATUS } from '../../constants/availabilityStatus';
import useAvailabilitySocket from '../../hooks/useAvailabilitySocket';
import ListingStatusNotification from './ListingStatusNotification';
import ListingDetailsEditor, {
  normalizeListingDetailsValue,
  validateListingDetailsValues
} from './ListingDetailsEditor';

const steps = ['basicInfo', 'details', 'pricing', 'images', 'availability'];

// Constante pour le temps minimum d'affichage de l'overlay
const MIN_OVERLAY_DISPLAY_TIME = 1500; // en millisecondes
const WEEK_DAYS_ISO = [1, 2, 3, 4, 5, 6, 7];

// Base URL for uploads (without /api/v1)
const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

const getUploadedFileUrl = (path) => {
  if (!path) return null;
  if (typeof path !== 'string') return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return `${UPLOADS_BASE_URL}${path}`;
  return `${UPLOADS_BASE_URL}/${path}`;
};

const CreateListingForm = () => {
  const { t } = useTranslation(['common','listing']);
  const navigate = useNavigate();
  const { id } = useParams(); // Pour détecter le mode édition
  const { user } = useAuth();
  const { showNotification } = useNotifications();
  const { categoriesList, loading: categoriesLoading } = useCategories();
  const { getCurrencySymbol, currentCurrency, getCurrencySelectOptions } = useCurrency();
  const getCategoryName = useCategoryName();
  const getCategoryDescription = useCategoryDescription();
  const listingCategories = useMemo(
    () => (categoriesList || []).filter((category) => !category?.context || category.context === 'rental' || category.context === 'both'),
    [categoriesList]
  );
  
  // Fonction pour obtenir le symbole de la devise sélectionnée dans le formulaire
  const getSelectedCurrencySymbol = () => {
    // Si nous avons les informations de devise stockées, les utiliser
    if (currentCurrencyInfo && currentCurrencyInfo.code === formData.currency) {
      return currentCurrencyInfo.symbol;
    }
    // Sinon, utiliser l'utilitaire comme fallback
    return getCurrencySymbol(formData.currency) || '€';
  };

  // Fonction pour obtenir le nom de la devise pour l'affichage
  const getCurrencyDisplayName = () => {
    const currencyOptions = getCurrencySelectOptions();
    const selectedOption = currencyOptions.find(option => option.value === formData.currency);
    
    // Si la devise est dans notre liste supportée, utiliser son nom complet
    if (selectedOption) {
      return selectedOption.label;
    }
    
    // Si nous avons les informations complètes de la devise (nom + symbole)
    if (currentCurrencyInfo && currentCurrencyInfo.code === formData.currency) {
      return `${currentCurrencyInfo.name} (${currentCurrencyInfo.symbol})`;
    }
    
    // Sinon, afficher juste le code de la devise
    return formData.currency || 'Euro';
  };
  
  const isEditMode = Boolean(id); // Mode édition si nous avons un ID
  
  // États pour les accordéons (ouvert par défaut : section informations générales)
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    address: false,
    pricing: true,
    images: true,
    availability: true
  });

  // État pour les paramètres de réservation avancés (booking_settings JSONB)
  const [bookingSettings, setBookingSettings] = useState({
    unit: 'day',
    pricing_mode: 'per_unit',
    capacity_mode: 'exclusive',
    date_range_mode: 'days', // 'days' (location matériel) | 'nights' (hébergement)
    min_duration: '',
    max_duration: '',
    slot_duration: '',
    buffer_before: 0,
    buffer_after: 0,
    opening_hours: [], // [{ day: 1-7, from: 'HH:mm', to: 'HH:mm' }]
    // Horaires check-in/check-out pour les biens immobiliers
    checkin_time: '14:00',
    checkout_time: '12:00',
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode); // Loading des données existantes
  const [existingListing, setExistingListing] = useState(null); // Stocke le listing complet en mode édition
  
  // États pour l'overlay de soumission
  const [submitting, setSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState('');

  // Track initial category when editing, to avoid auto-applying preset on first load
  const initialEditCategoryIdRef = React.useRef(null);
  const lastAppliedPresetCategoryIdRef = React.useRef(null);
  // Guard: empêche le fetch de données edition de se relancer après le premier chargement
  // (évite l'écrasement des saisies utilisateur lors des re-renders)
  const editDataLoadedForIdRef = React.useRef(null);
  // Devise d'origine du listing en mode édition (immuable après chargement)
  const originalCurrencyRef = React.useRef(null);
  
  // Référence pour le conteneur principal du formulaire
  const formContainerRef = React.useRef(null);
  const keepAvailabilityExpandedRef = React.useRef(false);
  // Référence pour l'alerte d'erreur currency mismatch (pour auto-scroll)
  const currencyErrorAlertRef = React.useRef(null);
  const listingDetailsEditorRef = React.useRef(null);
  const listingDetailsDefinitionsRef = React.useRef([]);
  const listingDetailsStatusRef = React.useRef({ loading: false, fetchError: null });

  // Refs pour le focus automatique sur les champs en erreur
  const fieldRefs = React.useRef({
    title: null,
    description: null,
    categoryId: null,
    country_code: null,
    city: null,
    address: null,
    destinationAddress: null,
    priceHourly: null,
    priceDaily: null,
    priceWeekly: null,
    priceMonthly: null,
    pricePerSlot: null,
    depositAmount: null,
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '', // Changé de 'category' à 'categoryId' pour correspondre au backend
    // Type d'offre et mode de réservation
    offer_type: 'equipment',
    booking_mode: 'date_range',
    capacity_default: '',   // Places max par créneau (axe capacité)
    stock_quantity: 1,      // Unités physiques disponibles (axe quantité)
    // Champs d'adresse étendus
    address: '',
    street_name: '',
    street_number: '',
    city: '',
    postal_code: '',
    region: '',
    country: '',
    country_code: '',
    // Initialiser avec des coordonnées par défaut (Paris) au lieu de null
    latitude: 48.8566,
    longitude: 2.3522,
    place_id: '',
    // Prix
    priceHourly: '',
    priceDaily: '',
    priceWeekly: '',
    priceMonthly: '',
    pricePerSlot: '',
    depositAmount: '0',
    currency: currentCurrency || 'EUR', // Initialiser avec la devise actuelle, mais permettre le changement
    images: [],
    details: {}
  });

  const [errors, setErrors] = useState({});
  const [bookingSettingsFieldErrors, setBookingSettingsFieldErrors] = useState({});
  const [bookingSettingsGlobalErrors, setBookingSettingsGlobalErrors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [currencyUpdateKey, setCurrencyUpdateKey] = useState(0);
  const [currentCurrencyInfo, setCurrentCurrencyInfo] = useState(null); // Pour forcer le re-rendu des devises
  
  // États pour la gestion des disponibilités
  const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(Date.now());
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [availabilityFormMode, setAvailabilityFormMode] = useState('add');
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [availabilityPeriods, setAvailabilityPeriods] = useState([]);
  // État pour traquer les modifications de périodes
  const [modifiedAvailabilityIds, setModifiedAvailabilityIds] = useState([]); // Traquer les modifications
  const [deletedAvailabilityIds, setDeletedAvailabilityIds] = useState([]); // Traquer les suppressions
  // Plages de dates des périodes supprimées localement (persiste entre les re-mounts du calendrier)
  const [deletedPeriodDateRanges, setDeletedPeriodDateRanges] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);

  const isAvailabilityPeriodPast = useCallback((period) => {
    if (!period) return false;

    const slotEnd = period?.reservation?.end_at || period?.end_at;
    if (slotEnd) {
      return moment(slotEnd).isSameOrBefore(moment());
    }

    return moment(period.available_to || period.end_date).isSameOrBefore(moment().startOf('day'));
  }, []);

  const isImmutableAvailabilityPeriod = useCallback((period) => {
    if (!period) return false;
    return Boolean(period.reservation_id || period.reservation?.id) || isAvailabilityPeriodPast(period);
  }, [isAvailabilityPeriodPast]);

  // ── Wizard par étapes ─────────────────────────────────────────────────────
  // Étape 0: Ressource (infos + images)
  // Étape 1: Localisation
  // Étape 2: Réservation (mode + quantité + capacité + planning)
  // Étape 3: Tarification + Disponibilités
  const [wizardStep, setWizardStep] = useState(0);
  // Bannière de preset catégorie (message temporaire affiché quand preset auto-appliqué)
  const [presetAppliedBanner, setPresetAppliedBanner] = useState(null); // null | { categoryName: string }

  const WIZARD_STEPS = [
    { key: 'resource',     label: t('listing:wizard.steps.resource'),      sections: ['basicInfo', 'images'] },
    { key: 'location',     label: t('listing:wizard.steps.location'),   sections: ['address'] },
    // La réservation est rendue par <BookingSettingsStep /> (accordéons internes),
    // donc aucune section parent à ouvrir ici.
    { key: 'booking',      label: t('listing:wizard.steps.booking'),    sections: [] },
    { key: 'pricing',      label: t('listing:wizard.steps.pricing'),   sections: ['images', 'pricing', 'availability'] },
  ];

  useEffect(() => {
    if (Object.keys(bookingSettingsFieldErrors).length > 0) {
      setBookingSettingsFieldErrors({});
    }
    if (bookingSettingsGlobalErrors.length > 0) {
      setBookingSettingsGlobalErrors([]);
    }
  }, [bookingSettings, formData?.booking_mode]);

  // ── Synchronisation pricing_mode en mode partagé ─────────────────────────
  // Règle : stock=1 → per_seat, stock>1 → per_unit_per_seat.
  // Se déclenche aussi quand le stock change (ex. owner modifie Q2 après Q3b).
  useEffect(() => {
    if (formData?.booking_mode !== 'time_slot_capacity') return;
    const stockQty = parseInt(formData?.stock_quantity, 10) || 1;
    setBookingSettings((prev) => {
      const pm = prev?.pricing_mode;
      if (pm === 'flat') return prev;
      if (!pm || pm === 'per_unit') {
        return { ...(prev || {}), pricing_mode: stockQty > 1 ? 'per_unit_per_seat' : 'per_seat' };
      }
      if (stockQty > 1 && pm === 'per_seat') return { ...(prev || {}), pricing_mode: 'per_unit_per_seat' };
      if (stockQty === 1 && pm === 'per_unit_per_seat') return { ...(prev || {}), pricing_mode: 'per_seat' };
      return prev;
    });
  }, [formData?.booking_mode, formData?.stock_quantity]);

  // ── Auto-apply booking preset from selected category ─────────────────────
  // Quand l'owner sélectionne une catégorie, on vérifie si elle a un preset.
  // Si oui, on pré-remplit booking_mode + bookingSettings + stock_quantity.
  // En mode édition: ne pas s'appliquer au chargement initial, mais s'applique
  // si l'utilisateur change la catégorie par rapport à celle d'origine.
  useEffect(() => {
    // Attendre que la liste soit chargée
    if (!listingCategories || listingCategories.length === 0) return;
    if (!formData.categoryId) return;

    // En édition: mémoriser la catégorie initiale et ne pas auto-appliquer tant
    // que l'utilisateur n'a pas explicitement changé de catégorie.
    if (isEditMode) {
      if (initialEditCategoryIdRef.current === null) {
        // Premier render post-fetch : mémoriser la catégorie d'origine et ne pas appliquer.
        // On ne retourne ici QUE lors du chargement initial, pas si l'utilisateur
        // re-sélectionne explicitement la même catégorie plus tard.
        initialEditCategoryIdRef.current = String(formData.categoryId);
        return;
      }
      const currentId = String(formData.categoryId);
      // Éviter un double-apply si le preset vient d'être appliqué pour cette catégorie
      if (lastAppliedPresetCategoryIdRef.current === currentId) return;
      // NOTE : on laisse passer même si currentId === initialEditCategoryIdRef.current,
      // car l'utilisateur a explicitement re-sélectionné cette catégorie après en avoir
      // changé — les paramètres de réservation doivent se mettre à jour.
    }

    const cat = listingCategories.find((c) => String(c.id) === String(formData.categoryId));
    
    // Auto-set offer_type depuis la catégorie si elle en a un défini
    if (cat?.offer_type) {      
      setFormData((prev) => {
        if (prev.offer_type === cat.offer_type) return prev; // pas de re-render inutile
        return { ...prev, offer_type: cat.offer_type };
      });
    } else {
      console.warn('[CreateListingForm] ATTENTION: catégorie sans offer_type défini. La géolocalisation conditionnelle (ride_share) ne sera pas activée.');
    }

    if (!cat?.booking_settings_preset?.booking_mode) return;

    const preset = cat.booking_settings_preset;
    // Appliquer le booking_mode + stock_quantity depuis le preset
    setFormData((prev) => ({
      ...prev,
      booking_mode: preset.booking_mode,
      ...(preset.stock_quantity ? { stock_quantity: preset.stock_quantity } : {}),
      ...((preset.capacity_default !== undefined && preset.capacity_default !== null && String(preset.capacity_default).trim() !== '')
        ? { capacity_default: String(preset.capacity_default) }
        : {}),
    }));
    // Appliquer les bookingSettings depuis le preset (hors booking_mode et stock_quantity)
    const { booking_mode: _bm, stock_quantity: _sq, capacity_default: _cd, ...settingsOnly } = preset;
    setBookingSettings((prev) => ({ ...prev, ...settingsOnly }));
    // Afficher la bannière avec le nom traduit de la catégorie
    const translatedCategoryName = getCategoryName(cat);
    setPresetAppliedBanner({
      categoryName: translatedCategoryName || '',
    });

    if (isEditMode) {
      lastAppliedPresetCategoryIdRef.current = String(formData.categoryId);
    }
  }, [formData.categoryId, listingCategories, isEditMode]);

  // FIX: Nettoyer les prix orphelins quand l'unité de facturation change
  // Évite d'envoyer au backend des prix stale pour des modes inactifs
  useEffect(() => {
    const unit = bookingSettings?.unit;
    if (!unit) return;
    setFormData(prev => {
      const patch = {};
      let needsPatch = false;
      if (unit === 'slot') {
        // En mode créneau, nettoyer les prix jour/heure (garder pricePerSlot)
        if (prev.priceHourly) { patch.priceHourly = ''; needsPatch = true; }
        if (prev.priceDaily)  { patch.priceDaily = '';  needsPatch = true; }
        if (prev.priceWeekly) { patch.priceWeekly = ''; needsPatch = true; }
        if (prev.priceMonthly){ patch.priceMonthly = ''; needsPatch = true; }
      } else if (unit === 'hour') {
        // En mode heure, nettoyer le prix créneau (garder prix horaire + jour/semaine/mois)
        if (prev.pricePerSlot) { patch.pricePerSlot = ''; needsPatch = true; }
      } else if (unit === 'day') {
        // En mode jour, nettoyer prix créneau et horaire
        if (prev.pricePerSlot) { patch.pricePerSlot = ''; needsPatch = true; }
        if (prev.priceHourly)  { patch.priceHourly = '';  needsPatch = true; }
      }
      return needsPatch ? { ...prev, ...patch } : prev;
    });
  }, [bookingSettings?.unit]);

  const parsePositiveInt = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? null : n;
  };

  const parseTimeToMinutes = (time) => {
    if (!time || typeof time !== 'string' || !time.includes(':')) return null;
    const [h, m] = time.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const validateBookingSettingsConsistency = () => {
    const fieldErrors = {};
    const globalErrors = [];

    const bookingMode = formData?.booking_mode;
    const isSlotBased = bookingMode === 'time_slot' || bookingMode === 'time_slot_capacity';
    const isShared = bookingMode === 'time_slot_capacity';

    const minDuration = parsePositiveInt(bookingSettings?.min_duration);
    const maxDuration = parsePositiveInt(bookingSettings?.max_duration);
    const rawSlotDuration = parsePositiveInt(bookingSettings?.slot_duration);
    const slotDuration = rawSlotDuration || (isSlotBased ? 0 : 60); // Ne pas fallback à 60 en slot-based si vide
    const bufferBefore = Math.max(0, parsePositiveInt(bookingSettings?.buffer_before) || 0);
    const bufferAfter = Math.max(0, parsePositiveInt(bookingSettings?.buffer_after) || 0);

    // Validation mode non sélectionné
    if (!bookingMode) {
      globalErrors.push(t('listing:bookingSettingsErrors.selectBookingMode'));
    }

    if (minDuration !== null && minDuration < 1) {
      fieldErrors.min_duration = t('listing:bookingSettingsErrors.minDurationRequired');
    }
    if (maxDuration !== null && maxDuration < 1) {
      fieldErrors.max_duration = t('listing:bookingSettingsErrors.maxDurationRequired');
    }
    if (minDuration !== null && maxDuration !== null && maxDuration < minDuration) {
      fieldErrors.max_duration = t('listing:bookingSettingsErrors.maxDurationMustBeGreater');
      globalErrors.push(t('listing:bookingSettingsErrors.durationInconsistent'));
    }

    // Validation capacity_default en mode partagé
    if (isShared) {
      const capacity = parsePositiveInt(formData?.capacity_default);
      if (capacity === null || capacity < 2) {
        fieldErrors.capacity_default = t('listing:bookingSettingsErrors.capacityMinRequired');
        globalErrors.push(t('listing:bookingSettingsErrors.capacityModeMessage'));
      }
    }

    if (isSlotBased) {
      // slot_duration est obligatoire en mode créneau
      if (!rawSlotDuration || rawSlotDuration <= 0) {
        fieldErrors.slot_duration = t('listing:bookingSettingsErrors.slotDurationRequired');
        globalErrors.push(t('listing:bookingSettingsErrors.slotDurationDefineMessage'));
      } else if (slotDuration < 5) {
        fieldErrors.slot_duration = t('listing:bookingSettingsErrors.slotDurationMinutes');
      }

      const openingHours = Array.isArray(bookingSettings?.opening_hours)
        ? bookingSettings.opening_hours
        : [];

      const openingHoursValidationErrors = [];
      for (const h of openingHours) {
        const day = parseInt(h?.day, 10);
        const from = parseTimeToMinutes(h?.from);
        const to = parseTimeToMinutes(h?.to);
        if (!WEEK_DAYS_ISO.includes(day)) {
          openingHoursValidationErrors.push(t('listing:bookingSettingsErrors.invalidDayOpeningHours', { day: h?.day }));
          continue;
        }
        if (from === null || to === null || to <= from) {
          openingHoursValidationErrors.push(t('listing:bookingSettingsErrors.invalidTimeRange', { day, from: h?.from, to: h?.to }));
        }
      }

      if (openingHoursValidationErrors.length > 0) {
        fieldErrors.opening_hours = t('listing:bookingSettingsErrors.invalidOpeningHours');
        globalErrors.push(...openingHoursValidationErrors);
      }

      if (openingHoursValidationErrors.length === 0) {
        const effectiveOpeningHours = openingHours.length > 0
          ? openingHours
          : WEEK_DAYS_ISO.map((day) => ({ day, from: '00:00', to: '23:59' }));

        const maxWindowMinutes = Math.max(
          ...effectiveOpeningHours.map((h) => parseTimeToMinutes(h.to) - parseTimeToMinutes(h.from))
        );

        const slotStep = slotDuration + bufferBefore + bufferAfter;
        const feasibleConsecutiveSlots = maxWindowMinutes < slotDuration
          ? 0
          : Math.floor((maxWindowMinutes - slotDuration) / slotStep) + 1;

        if (feasibleConsecutiveSlots <= 0) {
          fieldErrors.opening_hours = t('listing:bookingSettingsErrors.noSlotGeneratable');
          globalErrors.push(t('listing:bookingSettingsErrors.configurationImpossible'));
        }

        if (feasibleConsecutiveSlots > 0) {
          if (minDuration !== null && minDuration > feasibleConsecutiveSlots) {
            fieldErrors.min_duration = t('listing:bookingSettingsErrors.maxPossibleDuration', { count: feasibleConsecutiveSlots });
            globalErrors.push(t('listing:bookingSettingsErrors.minDurationTooLarge', { count: feasibleConsecutiveSlots }));
          }
          if (maxDuration !== null && maxDuration > feasibleConsecutiveSlots) {
            fieldErrors.max_duration = t('listing:bookingSettingsErrors.maxPossibleDuration', { count: feasibleConsecutiveSlots });
            globalErrors.push(t('listing:bookingSettingsErrors.maxDurationTooLarge', { count: feasibleConsecutiveSlots }));
          }
        }
      }
    }

    return {
      isValid: Object.keys(fieldErrors).length === 0 && globalErrors.length === 0,
      fieldErrors,
      globalErrors,
    };
  };

  const validateWizardStep = (step) => {
    if (step === 0) {
      const e = validateBasicInfo();
      if (Object.keys(e).length > 0) { 
        setErrors(e);
        scrollToFirstError(e);
        return false; 
      }

      const detailsValidation = validateDetailsSection();
      if (!detailsValidation.isValid) {
        showNotification(
          detailsValidation.message || t('listing:details.errors.formInvalid', 'Veuillez corriger les erreurs ci-dessous.'),
          'error'
        );
        return false;
      }
    }
    if (step === 1) {
      const e = validateAddress();
      if (Object.keys(e).length > 0) { 
        setErrors(e);
        scrollToFirstError(e);
        return false; 
      }
    }
    if (step === 2) {
      const bookingValidation = validateBookingSettingsConsistency();
      setBookingSettingsFieldErrors(bookingValidation.fieldErrors);
      setBookingSettingsGlobalErrors(bookingValidation.globalErrors);
      if (!bookingValidation.isValid) {
        showNotification(t('listing:bookingSettingsErrors.incohesiveParameters'), 'error');
        return false;
      }
    }
    if (step === 3) {
      const e = validatePricing();
      if (Object.keys(e).length > 0) { 
        setErrors(e);
        scrollToFirstError(e);
        return false; 
      }
    }
    setErrors({});
    return true;
  };

  const handleWizardNext = () => {
    if (!validateWizardStep(wizardStep)) return;
    const nextStep = wizardStep + 1;
    setWizardStep(nextStep);
    // Ouvrir la première section de la prochaine étape
    const firstSection = WIZARD_STEPS[nextStep]?.sections[0];
    if (firstSection) {
      setExpandedSections(prev => ({
        ...prev,
        [firstSection]: true
      }));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleWizardBack = () => {
    const prevStep = wizardStep - 1;
    setWizardStep(prevStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const parentIdsWithChildren = useMemo(() => {
    const parentIds = new Set();
    for (const cat of listingCategories) {
      const parentId = cat?.parent_id || cat?.parentId || cat?.parent?.id;
      if (parentId) parentIds.add(String(parentId));
    }
    return parentIds;
  }, [listingCategories]);

  const categoryTreeRoots = useMemo(() => {
    const list = listingCategories;
    const byId = new Map();

    for (const cat of list) {
      if (!cat || cat.id === undefined || cat.id === null) continue;
      byId.set(String(cat.id), { ...cat, children: [] });
    }

    const roots = [];
    for (const node of byId.values()) {
      const parentId = node?.parent_id || node?.parentId || node?.parent?.id;
      const parentKey = parentId ? String(parentId) : null;
      if (parentKey && byId.has(parentKey)) {
        byId.get(parentKey).children.push(node);
      } else {
        roots.push(node);
      }
    }

    return roots;
  }, [listingCategories]);

  // Fonctions utilitaires pour les catégories
  const isLeafCategory = (category) => {
    if (!category) return false;
    const idKey = category.id !== undefined && category.id !== null ? String(category.id) : null;
    const hasChildrenInFlatList = idKey ? parentIdsWithChildren.has(idKey) : false;
    const hasChildrenInTree = Array.isArray(category.children) && category.children.length > 0;
    return !hasChildrenInFlatList && !hasChildrenInTree;
  };

  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [categorySearchQuery, setCategorySearchQuery] = useState('');

  // Fonction pour obtenir le chemin complet traduit d'une catégorie
  const getCategoryFullPath = useCallback((categoryId) => {
    const category = listingCategories.find(cat => cat.id === categoryId);
    if (!category) return '';
    
    // Debug pour vérifier la structure des catégories
    console.log('Category found:', category);
    console.log('Category structure keys:', Object.keys(category));
    
    // Si la catégorie a déjà un fullPath, l'utiliser et le traduire
    if (category.fullPath) {
      console.log('Using existing fullPath:', category.fullPath);
      // Diviser le chemin existant et traduire chaque partie
      const pathParts = category.fullPath.split(' > ');
      const translatedParts = pathParts.map(part => {
        // Trouver la catégorie correspondant à ce nom
        const cat = listingCategories.find(c => c.name === part);
        return cat ? getCategoryName(cat) : part;
      });
      return translatedParts.join(' > ');
    }
    
    // Sinon, construire le chemin récursivement
    const buildTranslatedPath = (cat, pathCache = new Map()) => {
      // Vérifier le cache d'abord
      if (pathCache.has(cat.id)) {
        return pathCache.get(cat.id);
      }
      
      let translatedPath;
      // Vérifier différents champs pour le parent (parent_id, parentId, parent)
      const parentId = cat.parent_id || cat.parentId || (cat.parent && cat.parent.id);
      
      if (!parentId) {
        // Catégorie racine
        translatedPath = getCategoryName(cat);
      } else {
        // Catégorie avec parent - construire le chemin récursivement
        const parent = listingCategories.find(c => c.id === parentId);
        if (parent) {
          translatedPath = `${buildTranslatedPath(parent, pathCache)} > ${getCategoryName(cat)}`;
        } else {
          translatedPath = getCategoryName(cat);
        }
      }
      
      // Mettre en cache le résultat
      pathCache.set(cat.id, translatedPath);
      return translatedPath;
    };
    
    const result = buildTranslatedPath(category);
    console.log('Final translated path:', result);
    return result;
  }, [listingCategories, getCategoryName]);

  // Fonction pour filtrer les catégories selon la recherche
  const filterCategoriesList = useCallback((categories, query) => {
    if (!query) return categories;
    
    return categories.filter(cat => {
      // Utiliser le chemin traduit pour la recherche
      const translatedPath = getCategoryFullPath(cat.id);
      return translatedPath.toLowerCase().includes(query.toLowerCase()) ||
             getCategoryName(cat).toLowerCase().includes(query.toLowerCase());
    });
  }, [getCategoryFullPath, getCategoryName]);

  const toggleCategoryExpansion = (categoryId) => {
    const key = String(categoryId);
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedCategories(newExpanded);
  };

  const renderCategoryTree = (nodes, level = 0) => {
    return nodes.map((node) => {
      const isLeaf = isLeafCategory(node);
      const nodeKey = String(node.id);
      const isExpanded = expandedCategories.has(nodeKey);
      const isSelected = String(formData.categoryId) === String(node.id);

      const nodeImageUrl = getUploadedFileUrl(node.imageUrl);

      return (
        <Box key={node.id}>
          <ListItem
            disablePadding
            sx={{
              pl: level * 3,
              mb: 0.5,
            }}
          >
            <ListItemButton
              selected={isSelected}
              onClick={() => {
                if (isLeaf) {
                  handleInputChange('categoryId', node.id);
                } else {
                  toggleCategoryExpansion(nodeKey);
                }
              }}
              disabled={!isLeaf && node.children && node.children.length === 0}
              sx={{
                borderRadius: '8px',
                mx: 1,
                transition: 'all 0.3s ease-in-out',
                background: isSelected
                  ? 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)'
                  : 'transparent',
                color: isSelected ? 'white' : 'inherit',
                '&:hover': {
                  background: isSelected
                    ? 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                    : 'linear-gradient(145deg, #ffffff 0%, #f0f0f0 100%)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(98, 0, 238, 0.15)',
                },
                '&.Mui-selected': {
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 74,
                  color: isSelected ? 'white' : 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!isLeaf && (
                    <Box
                      sx={{
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </Box>
                  )}

                  {nodeImageUrl ? (
                    <Avatar
                      variant="rounded"
                      src={nodeImageUrl}
                      alt={getCategoryName(node)}
                      sx={{ width: 28, height: 28, borderRadius: '8px' }}
                      imgProps={{ loading: 'lazy' }}
                    />
                  ) : isLeaf ? (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: isSelected
                          ? 'rgba(255,255,255,0.2)'
                          : 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <InsertDriveFileIcon fontSize="small" sx={{ color: 'white', fontSize: '14px' }} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: isSelected
                          ? 'rgba(255,255,255,0.2)'
                          : 'linear-gradient(145deg, #f0f0f0 0%, #e0e0e0 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FolderIcon fontSize="small" sx={{ color: isSelected ? 'white' : '#666', fontSize: '14px' }} />
                    </Box>
                  )}
                </Box>
              </ListItemIcon>

              <ListItemText
                primary={getCategoryName(node)}
                primaryTypographyProps={{
                  fontWeight: isSelected ? 600 : (isLeaf ? 500 : 600),
                  color: isSelected ? 'white' : (isLeaf ? 'text.primary' : 'text.secondary'),
                  fontSize: isLeaf ? '0.875rem' : '0.9rem',
                }}
              />

              {isSelected && (
                <CheckCircleIcon
                  fontSize="small"
                  sx={{
                    color: 'white',
                    ml: 1,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.8, transform: 'scale(1.1)' },
                      '100%': { opacity: 1, transform: 'scale(1)' },
                    },
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>

          {!isLeaf && node.children && node.children.length > 0 && (
            <Collapse
              in={isExpanded}
              timeout="auto"
              unmountOnExit
              sx={{
                '& .MuiCollapse-wrapper': {
                  background:
                    level % 2 === 0
                      ? 'linear-gradient(to right, rgba(98, 0, 238, 0.02) 0%, transparent 100%)'
                      : 'linear-gradient(to right, rgba(3, 218, 196, 0.02) 0%, transparent 100%)',
                },
              }}
            >
              {renderCategoryTree(node.children, level + 1)}
            </Collapse>
          )}
        </Box>
      );
    });
  };

  // Charger dynamiquement la liste des pays au montage
  useEffect(() => {
    const fetchCountries = async () => {
      setCountryLoading(true);
      try {
        const res = await api.get('https://restcountries.com/v3.1/all?fields=name,cca2');
        const countryList = res.data
          .map(c => ({ code: c.cca2, name: c.name.common }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setCountries(countryList);
      } catch (e) {
        setCountries([]);
      } finally {
        setCountryLoading(false);
      }
    };
    fetchCountries();
  }, []);

  // Auto-scroll vers l'erreur currency mismatch quand elle apparaît
  useEffect(() => {
    if (errors.country_code) {
      const timer = setTimeout(() => {
        if (currencyErrorAlertRef.current) {
          const rect = currencyErrorAlertRef.current.getBoundingClientRect();
          window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - 100), behavior: 'smooth' });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [errors.country_code]);

  // Synchroniser la devise avec le pays en mode création
  useEffect(() => {
    if (!isEditMode && formData.country_code) {
      const countryCurrency = getCurrencyByCountry(formData.country_code);
      if (countryCurrency !== formData.currency) {
        setFormData(prev => ({
          ...prev,
          currency: countryCurrency
        }));
      }
    }
  }, [formData.country_code, isEditMode]);

  // Synchronisation dynamique de la devise avec le pays (utilise l'utilitaire)
  useEffect(() => {
    const updateCurrencyDynamically = async () => {
      if (formData.country_code) {
        try {
          const currencyInfo = await getCurrencyForCountry(formData.country_code);

          // En mode édition : valider la compatibilité devise sans modifier la devise
          if (isEditMode && originalCurrencyRef.current) {
            if (currencyInfo.code !== originalCurrencyRef.current) {
              // Devise incompatible → erreur sur le champ localisation
              setErrors(prev => ({
                ...prev,
                country_code: t('listing.errors.currencyMismatch', {
                  from: originalCurrencyRef.current,
                  to: currencyInfo.code
                })
              }));
            } else {
              // Devise compatible → effacer l'erreur
              setErrors(prev => {
                const newErr = { ...prev };
                delete newErr.country_code;
                return newErr;
              });
            }
            // Mettre à jour les infos devise pour l'affichage (sans changer le code)
            setCurrentCurrencyInfo(currencyInfo);
            return;
          }

          if (currencyInfo.code !== formData.currency) {
            console.log(`[CurrencySync] Updating currency from ${formData.currency} to ${currencyInfo.code} for country ${formData.country_code}`);
            
            // Stocker les informations complètes de la devise
            setCurrentCurrencyInfo(currencyInfo);
            
            setFormData(prev => ({
              ...prev,
              currency: currencyInfo.code
            }));
            setCurrencyUpdateKey(prev => prev + 1); // Forcer le re-rendu des champs de devise
          } else if (!currentCurrencyInfo || currentCurrencyInfo.code !== currencyInfo.code) {
            // Même devise mais mise à jour des informations (symbole, etc.)
            setCurrentCurrencyInfo(currencyInfo);
            setCurrencyUpdateKey(prev => prev + 1);
          }
        } catch (error) {
          console.warn('[CurrencySync] Error updating currency:', error);
        }
      }
    };

    // Délai pour éviter les appels répétés lors des changements rapides
    const timeoutId = setTimeout(updateCurrencyDynamically, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.country_code]);

  // Charger les données existantes en mode édition
  useEffect(() => {
    if (isEditMode && id && !categoriesLoading && listingCategories.length > 0) {
      // Guard : ne charger qu'une seule fois par listing id pour ne pas écraser les saisies utilisateur
      if (editDataLoadedForIdRef.current === id) return;
      editDataLoadedForIdRef.current = id;

      const fetchListingData = async () => {
        setInitialLoading(true);
        try {
          console.log('Fetching listing data with cache bypass...');
          // Ajouter un timestamp pour contourner le cache
          const timestamp = new Date().getTime();
          const response = await api.get(`/listings/${id}?_t=${timestamp}`);
          const listing = response.data.listing;
          
          console.log('Fresh listing data loaded:', listing);
          console.log('Currency from listing:', listing.currency, 'Current currency:', currentCurrency);
          
          // Stocker le listing complet pour la notification de statut
          setExistingListing(listing);
          
          // Debug des coordonnées avant conversion
          console.log('=== COORDINATES DEBUG ===');
          console.log('Raw latitude:', listing.latitude, 'Type:', typeof listing.latitude);
          console.log('Raw longitude:', listing.longitude, 'Type:', typeof listing.longitude);
          console.log('Number(latitude):', Number(listing.latitude));
          console.log('Number(longitude):', Number(listing.longitude));
          console.log('isNaN(Number(latitude)):', isNaN(Number(listing.latitude)));
          console.log('isNaN(Number(longitude)):', isNaN(Number(listing.longitude)));
          console.log('========================');
          
          // Vérifier que l'utilisateur est le propriétaire
          if (listing.owner?.id !== user?.id) {
            showNotification(t('errors.forbidden'), 'error');
            navigate('/my-listings');
            return;
          }
          
          // Remplir le formulaire avec les données existantes
          // Gérer les deux cas : categoryId numérique ou category string
          const foundCategory = typeof listing.category === 'string' ? 
            listingCategories.find(cat => cat.name === listing.category || cat.slug === listing.category) : 
            null;
          
          // Déterminer le country_code basé sur les données existantes
          let detectedCountryCode = listing.country_code || '';
          
          // Si country_code est vide mais qu'on a un nom de pays, essayer de le trouver
          if (!detectedCountryCode && listing.country) {
            console.log('Trying to find country_code from country name:', listing.country);
            const foundCountry = countries.find(c => 
              c.name.toLowerCase() === listing.country.toLowerCase() ||
              c.name.toLowerCase().includes(listing.country.toLowerCase()) ||
              listing.country.toLowerCase().includes(c.name.toLowerCase())
            );
            if (foundCountry) {
              detectedCountryCode = foundCountry.code;
              console.log('Found country_code from name lookup:', detectedCountryCode);
            }
          }
          
          console.log('Country detection final result:', {
            original_country_code: listing.country_code,
            original_country: listing.country,
            final_detected: detectedCountryCode
          });
          
          setFormData({
            title: listing.title || '',
            description: listing.description || '',
            // Gérer les deux cas : categoryId numérique ou category string
            categoryId: listing.categoryId || foundCategory?.id || '',
            address: listing.address || '',
            street_name: listing.street_name || '',
            street_number: listing.street_number || '',
            city: listing.city || '',
            postal_code: listing.postal_code || '',
            region: listing.region || '',
            country: listing.country || '',
            country_code: detectedCountryCode, // Utiliser le country_code détecté
            latitude: (() => {
              // Conversion robuste des coordonnées
              if (listing.latitude === null || listing.latitude === undefined || listing.latitude === '') return null;
              const lat = Number(listing.latitude);
              return (!isNaN(lat) && lat >= -90 && lat <= 90) ? lat : null;
            })(),
            longitude: (() => {
              // Conversion robuste des coordonnées
              if (listing.longitude === null || listing.longitude === undefined || listing.longitude === '') return null;
              const lng = Number(listing.longitude);
              return (!isNaN(lng) && lng >= -180 && lng <= 180) ? lng : null;
            })(),
            place_id: listing.place_id || '',
            priceHourly: listing.price_hourly || '',
            priceDaily: listing.price_daily || '',
            priceWeekly: listing.price_weekly || '',
            priceMonthly: listing.price_monthly || '',
            pricePerSlot: listing.booking_settings?.price_per_slot || listing.price_per_slot || '',
            depositAmount: listing.deposit_amount !== undefined && listing.deposit_amount !== null ? String(listing.deposit_amount) : '0',
            currency: listing.currency || getCurrencyByCountry(detectedCountryCode) || currentCurrency || 'EUR', // En édition, priorité à la devise existante
            // Champs de type/mode de réservation
            offer_type: listing.offer_type || 'equipment',
            booking_mode: listing.booking_mode || 'date_range',
            capacity_default: listing.capacity_default || '',
            stock_quantity: listing.stock_quantity || 1,
            images: listing.images || [], // Charger les images existantes
            details: normalizeListingDetailsValue(listing.details)
          });

          // Stocker la devise d'origine pour la validation en mode édition
          originalCurrencyRef.current = listing.currency || getCurrencyByCountry(detectedCountryCode) || currentCurrency || 'EUR';
          
          // Debug après conversion pour vérifier les coordonnées finales
          const finalLat = (() => {
            if (listing.latitude === null || listing.latitude === undefined || listing.latitude === '') return null;
            const lat = Number(listing.latitude);
            return (!isNaN(lat) && lat >= -90 && lat <= 90) ? lat : null;
          })();
          const finalLng = (() => {
            if (listing.longitude === null || listing.longitude === undefined || listing.longitude === '') return null;
            const lng = Number(listing.longitude);
            return (!isNaN(lng) && lng >= -180 && lng <= 180) ? lng : null;
          })();
          
          console.log('=== FINAL COORDINATES DEBUG ===');
          console.log('Final latitude in formData:', finalLat);
          console.log('Final longitude in formData:', finalLng);
          console.log('Will coordinates be valid?:', (finalLat !== null && finalLng !== null && !isNaN(finalLat) && !isNaN(finalLng)));
          console.log('==============================');

          // Ouvrir automatiquement les sections qui ont des données
          setExpandedSections({
            basicInfo: true, // Toujours ouvert
            address: !!(listing.address || listing.city),
            pricing: !!(listing.price_hourly || listing.price_daily || listing.price_weekly || listing.price_monthly),
            images: !!(listing.images && listing.images.length > 0),
            availability: true
          });

          // Charger les booking_settings existants
          // ET normaliser les valeurs corrompues héritées de la DB (capacity_mode / pricing_mode)
          if (listing.booking_settings && typeof listing.booking_settings === 'object') {
            const bm = listing.booking_mode || 'date_range';
            const raw = listing.booking_settings;
            const stockQty = parseInt(listing.stock_quantity, 10) || 1;
            const corrected = { ...raw };

            if (bm === 'time_slot_capacity') {
              corrected.capacity_mode = 'shared';
              // Règle unification per_seat / per_unit_per_seat selon le stock :
              //   stock=1  → per_seat (1 exemplaire, pas besoin de l'axe unité)
              //   stock>1  → per_unit_per_seat (bloque le bon nombre d'unités dans le stock)
              //   flat     → préservé
              //   per_unit → invalide → défaut selon stock
              if (corrected.pricing_mode !== 'flat') {
                if (!corrected.pricing_mode || corrected.pricing_mode === 'per_unit') {
                  corrected.pricing_mode = stockQty > 1 ? 'per_unit_per_seat' : 'per_seat';
                } else if (stockQty > 1 && corrected.pricing_mode === 'per_seat') {
                  corrected.pricing_mode = 'per_unit_per_seat';
                } else if (stockQty === 1 && corrected.pricing_mode === 'per_unit_per_seat') {
                  corrected.pricing_mode = 'per_seat';
                }
              }
            } else if (bm === 'time_slot') {
              corrected.capacity_mode = 'exclusive';
              if (corrected.pricing_mode === 'per_seat' || corrected.pricing_mode === 'per_unit_per_seat') {
                corrected.pricing_mode = 'per_unit';
              }
              if (!corrected.pricing_mode) corrected.pricing_mode = 'per_unit';
            } else {
              // date_range
              corrected.capacity_mode = 'exclusive';
              if (corrected.pricing_mode === 'per_seat' || corrected.pricing_mode === 'per_unit_per_seat') {
                corrected.pricing_mode = 'per_unit';
              }
              if (!corrected.pricing_mode) corrected.pricing_mode = 'per_unit';
              // Défauts horaires de début/fin pour tous les listings en mode date_range
              if (!corrected.checkin_time) corrected.checkin_time = '14:00';
              if (!corrected.checkout_time) corrected.checkout_time = '12:00';
            }

            setBookingSettings(prev => ({ ...prev, ...corrected }));
          }
          
        } catch (error) {
          console.error('Error loading listing data:', error);
          const errorMessage = getErrorMessage(error, t);
          showNotification(errorMessage, 'error');
          navigate('/my-listings');
        } finally {
          setInitialLoading(false);
        }
      };
      
      fetchListingData();
    }
  }, [isEditMode, id, categoriesLoading, listingCategories, user, showNotification, t, navigate, currentCurrency]);

  // Autocomplétion dynamique des villes
  const handleCityInputChange = useCallback(async (event, value) => {
    setFormData(prev => ({ ...prev, city: value }));
    
    if (!value || value.length < 2 || !formData.country_code) {
      setCityOptions([]);
      return;
    }
    
    setCityLoading(true);
    try {
      const res = await api.get('/listings/autocomplete-address', {
        params: { query: value, country_code: formData.country_code }
      });
      
      // Filtrer pour ne garder que les villes
      const validTypes = ['city', 'town', 'village', 'administrative', 'municipality'];
      const options = (res.data.suggestions || []).filter(opt => 
        validTypes.includes(opt.type)
      );
      
      setCityOptions(options);
    } catch (e) {
      console.error('Error in city autocomplete:', e);
      setCityOptions([]);
    } finally {
      setCityLoading(false);
    }
  }, [formData.country_code]);

  // Gestion des accordéons
  const handleAccordionChange = (section) => (event, isExpanded) => {
    if (section === 'availability') {
      setExpandedSections(prev => ({
        ...prev,
        availability: true
      }));
      return;
    }

    // Guard: empêcher la fermeture de la section disponibilités pendant une opération active
    if (section === 'availability' && !isExpanded && keepAvailabilityExpandedRef.current) {
      return;
    }
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded
    }));
    
    // Centrer la section dans l'écran quand elle s'ouvre
    if (isExpanded) {
      // Utiliser setTimeout pour attendre que l'animation d'ouverture soit terminée
      setTimeout(() => {
        const sectionElement = document.getElementById(`${section}-header`);
        if (sectionElement) {
          // Calculer la position pour afficher le début de la section avec un petit décalage
          const headerRect = sectionElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          
          // Position cible : début de l'en-tête avec un décalage de 20px vers le haut
          const targetPosition = headerRect.top + scrollTop - 20;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }, 300); // Délai pour laisser l'animation d'accordéon se terminer
    }
  };

  // Validation des sections
  const validateBasicInfo = () => {
    const newErrors = {};
    if (!formData.title || !formData.title.trim()) {
      newErrors.title = t('listing.errors.titleRequired');
    }
    if (!formData.description || !formData.description.trim()) {
      newErrors.description = t('listing.errors.descriptionRequired');
    }
    if (!formData.categoryId) {
      newErrors.categoryId = t('listing.errors.categoryRequired');
    }
    return newErrors;
  };

  const validateAddress = () => {
    const newErrors = {};
    
    if (!formData.country_code) {
      newErrors.country_code = t('listing.errors.countryRequired');
    }

    // En mode édition : vérifier la compatibilité devise/localisation
    if (isEditMode && originalCurrencyRef.current && formData.country_code) {
      const locationCurrency = getCurrencyByCountry(formData.country_code);
      if (locationCurrency && locationCurrency !== originalCurrencyRef.current) {
        newErrors.country_code = t('listing.errors.currencyMismatch', {
          from: originalCurrencyRef.current,
          to: locationCurrency
        });
      }
    }

    if (!formData.city || !formData.city.trim()) {
      newErrors.city = t('listing.errors.cityRequired');
    }
    if (!formData.address || !formData.address.trim()) {
      newErrors.address = t('listing.errors.addressRequired');
    }

    // Validation spécifique pour le covoiturage: Adresse de destination requise
    if (formData.offer_type === 'ride_share') {
      if (!bookingSettings.destinationAddress || !bookingSettings.destinationAddress.trim()) {
        newErrors.destinationAddress = t('listing.errors.destinationAddressRequired');
      }
    }

    // Vérifier que les coordonnées sont des nombres valides
    const lat = formData.latitude !== null && formData.latitude !== undefined ? Number(formData.latitude) : NaN;
    const lon = formData.longitude !== null && formData.longitude !== undefined ? Number(formData.longitude) : NaN;
    
    if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      newErrors.coordinates = t('listing.errors.coordinatesRequired');
    }
    return newErrors;
  };

  const validatePricing = () => {
    const newErrors = {};
    
    // Déterminer l'unité active pour savoir quel prix est requis
    const activeUnit = bookingSettings?.unit || 'day';
    
    // Au moins un prix doit être défini (cohérent avec l'unité choisie)
    const hasRelevantPrice =
      (activeUnit === 'hour' && formData.priceHourly) ||
      (activeUnit === 'slot' && formData.pricePerSlot) ||
      (activeUnit === 'day' && (formData.priceDaily || formData.priceWeekly || formData.priceMonthly)) ||
      // Fallback: n'importe quel prix rempli
      formData.priceHourly || formData.priceDaily || formData.priceWeekly || formData.priceMonthly || formData.pricePerSlot;
    
    if (!hasRelevantPrice) {
      newErrors.pricing = t('listing.errors.priceRequired');
      // Ajout d'un message contextuel selon l'unité
      if (activeUnit === 'slot') {
        const pricingMode = bookingSettings?.pricing_mode || 'per_unit';
        newErrors.pricePerSlot =
          pricingMode === 'per_seat'
            ? t('listing:pricingModes.errors.perSeat')
            : pricingMode === 'per_unit_per_seat'
            ? t('listing:pricingModes.errors.perUnitPerSeat')
            : pricingMode === 'flat'
            ? t('listing:pricingModes.errors.flat')
            : t('listing:pricingModes.errors.default');
      } else if (activeUnit === 'hour') {
        newErrors.priceHourly = t('listing:priceErrors.hourlyRequired');
      } else {
        newErrors.priceDaily = t('listing:priceErrors.dailyRequired');
      }
    }
    
    // Valider chaque prix individuellement (max 999999999999999.99 pour NUMERIC(15,2))
    // Limite élevée pour supporter les monnaies faibles (VND, IRR, IDR, etc.)
    const MAX_PRICE = 999999999999999.99;
    const MIN_PRICE = 0.01;
    
    if (formData.priceHourly) {
      const price = Number(formData.priceHourly);
      if (isNaN(price) || price < MIN_PRICE) {
        newErrors.priceHourly = t('listing.errors.priceInvalid');
      } else if (price > MAX_PRICE) {
        newErrors.priceHourly = t('listing.errors.priceTooHigh');
      }
    }
    
    if (formData.priceDaily) {
      const price = Number(formData.priceDaily);
      if (isNaN(price) || price < MIN_PRICE) {
        newErrors.priceDaily = t('listing.errors.priceInvalid');
      } else if (price > MAX_PRICE) {
        newErrors.priceDaily = t('listing.errors.priceTooHigh');
      }
    }
    
    if (formData.priceWeekly) {
      const price = Number(formData.priceWeekly);
      if (isNaN(price) || price < MIN_PRICE) {
        newErrors.priceWeekly = t('listing.errors.priceInvalid');
      } else if (price > MAX_PRICE) {
        newErrors.priceWeekly = t('listing.errors.priceTooHigh');
      }
    }
    
    if (formData.priceMonthly) {
      const price = Number(formData.priceMonthly);
      if (isNaN(price) || price < MIN_PRICE) {
        newErrors.priceMonthly = t('listing.errors.priceInvalid');
      } else if (price > MAX_PRICE) {
        newErrors.priceMonthly = t('listing.errors.priceTooHigh');
      }
    }
    
    if (formData.pricePerSlot) {
      const price = Number(formData.pricePerSlot);
      if (isNaN(price) || price < MIN_PRICE) {
        newErrors.pricePerSlot = t('listing.errors.priceInvalid');
      } else if (price > MAX_PRICE) {
        newErrors.pricePerSlot = t('listing.errors.priceTooHigh');
      }
    }
    
    // Validation du dépôt de garantie (obligatoire, minimum 0)
    const depositVal = formData.depositAmount === '' || formData.depositAmount === null || formData.depositAmount === undefined
      ? null
      : Number(formData.depositAmount);
    if (depositVal === null || isNaN(depositVal)) {
      newErrors.depositAmount = t('listing.errors.depositRequired');
    } else if (depositVal < 0) {
      newErrors.depositAmount = t('listing.errors.depositInvalid');
    } else if (depositVal > MAX_PRICE) {
      newErrors.depositAmount = t('listing.errors.depositTooHigh');
    }
    
    return newErrors;
  };

  // Fonction de validation pour la section de disponibilité
  const validateAvailability = () => {
    const newErrors = {};
    // Pas d'erreurs par défaut - la disponibilité est optionnelle lors de la création
    return newErrors;
  };
  
  // Fonction pour scroller vers la première erreur
  const scrollToFirstError = (errors) => {
    if (Object.keys(errors).length === 0) return;

    // Priorité 1 : Erreur currency mismatch en mode édition → scroller vers l'Alert d'erreur
    if (isEditMode && errors.country_code) {
      setTimeout(() => {
        if (currencyErrorAlertRef.current) {
          const rect = currencyErrorAlertRef.current.getBoundingClientRect();
          window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - 100), behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    // Mapping des champs d'erreur vers les sections d'accordéon
    const errorToSectionMap = {
      // Infos de base
      title: 'basicInfo',
      description: 'basicInfo',
      categoryId: 'basicInfo',
      offer_type: 'basicInfo',
      // Adresse
      country_code: 'address',
      city: 'address',
      address: 'address',
      street_number: 'address',
      street_name: 'address',
      postal_code: 'address',
      coordinates: 'address',
      destinationAddress: 'address',
      // Tarification
      pricing: 'pricing',
      priceHourly: 'pricing',
      priceDaily: 'pricing',
      priceWeekly: 'pricing',
      priceMonthly: 'pricing',
      pricePerSlot: 'pricing',
      depositAmount: 'pricing',
      currency: 'pricing',
      // Images
      images: 'images'
    };

    // Trouver la première erreur
    const firstErrorField = Object.keys(errors)[0];
    const sectionToOpen = errorToSectionMap[firstErrorField] || 'basicInfo';
    const sectionToWizardStepMap = {
      basicInfo: 0,
      address: 1,
      pricing: 3,
      images: 3
    };
    const targetWizardStep = sectionToWizardStepMap[sectionToOpen];

    // Ouvrir la section concernée
    setExpandedSections(prev => ({
      ...prev,
      [sectionToOpen]: true
    }));

    if (targetWizardStep !== undefined && targetWizardStep !== wizardStep) {
      setWizardStep(targetWizardStep);
    }

    // Attendre que l'accordéon s'ouvre puis tenter le focus
    setTimeout(() => {
      // 1. Utiliser la ref directe si elle existe
      const ref = fieldRefs.current[firstErrorField];
      // 2. Fallback : pour les champs sans ref propre (ex: champ générique 'pricing'),
      //    choisir le premier input visible de la section selon l'unité active
      const activePricingField =
        bookingSettings?.unit === 'hour' ? 'priceHourly'
        : bookingSettings?.unit === 'slot' ? 'pricePerSlot'
        : bookingSettings?.unit === 'day'  ? 'priceDaily'
        : 'depositAmount';
      const sectionFallback = {
        basicInfo: 'title',
        address: 'address',
        pricing: activePricingField,
        images: null,
      };
      const el = ref || fieldRefs.current[sectionFallback[sectionToOpen]] || fieldRefs.current['depositAmount'] || null;

      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          el.focus({ preventScroll: true });
          if (el.select) el.select();
        }, 300);
      }
    }, targetWizardStep !== undefined && targetWizardStep !== wizardStep ? 550 : 400); // Délai pour laisser l'animation se terminer
  };

  // Fonction pour ouvrir le modal de période de disponibilité
  const handleOpenPeriodModal = (mode = 'add', period = null) => {
    keepAvailabilityExpandedRef.current = true;
    setExpandedSections(prev => ({
      ...prev,
      availability: true
    }));
    setAvailabilityFormMode(mode);
    setSelectedPeriod(period);
    setIsFormModalOpen(true);
  };

  useEffect(() => {
    if (!keepAvailabilityExpandedRef.current) {
      return;
    }

    setExpandedSections(prev => {
      if (prev.availability) {
        return prev;
      }

      return {
        ...prev,
        availability: true
      };
    });

    if (!isFormModalOpen) {
      keepAvailabilityExpandedRef.current = false;
    }
  }, [availabilityPeriods, isFormModalOpen]);

  const isAvailabilityAccordionExpanded = true;

  // Statut de validation de chaque section
  const getSectionStatus = (section) => {
    switch (section) {
      case 'basicInfo':
        const basicErrors = validateBasicInfo();
        return Object.keys(basicErrors).length === 0 && 
               formData.title && formData.description && formData.categoryId;
      case 'address':
        const addressErrors = validateAddress();
        return Object.keys(addressErrors).length === 0 && 
               formData.country_code && formData.city && formData.address;
      case 'pricing':
        const pricingErrors = validatePricing();
        return Object.keys(pricingErrors).length === 0;
      case 'images':
        return formData.images.length > 0;
      case 'availability':
        // La disponibilité est considérée comme valide si au moins une période est définie
        // ou si nous sommes en mode création (ce sera géré automatiquement)
        return availabilityPeriods.length > 0 || !isEditMode;
      case 'bookingSettings':
        // Valide si au moins un paramètre non-défaut est configuré
        return !!(formData.offer_type && formData.booking_mode && (
          formData.stock_quantity > 1 ||
          formData.capacity_default ||
          bookingSettings.min_duration ||
          bookingSettings.max_duration ||
          bookingSettings.opening_hours.length > 0
        ));
      default:
        return false;
    }
  };

  // Icône de statut pour chaque section
  const getSectionIcon = (section) => {
    const isValid = getSectionStatus(section);
    const hasData = section === 'basicInfo' ? 
      (formData.title || formData.description || formData.categoryId) :
      section === 'address' ? 
      (formData.country_code || formData.city || formData.address || formData.latitude || formData.longitude) :
      section === 'pricing' ?
      (formData.priceHourly || formData.priceDaily || formData.priceWeekly || formData.priceMonthly || formData.depositAmount) :
      formData.images.length > 0;

    if (isValid) {
      return <CheckCircleIcon 
        color="success" 
        sx={{ 
          ml: 1,
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.8, transform: 'scale(1.05)' },
            '100%': { opacity: 1, transform: 'scale(1)' }
          }
        }} 
      />;
    } else if (hasData) {
      return <ErrorIcon 
        color="warning" 
        sx={{ 
          ml: 1,
          animation: 'shake 0.5s ease-in-out',
          '@keyframes shake': {
            '0%, 100%': { transform: 'translateX(0)' },
            '25%': { transform: 'translateX(-2px)' },
            '75%': { transform: 'translateX(2px)' }
          }
        }} 
      />;
    } else {
      return <InfoIcon 
        color="disabled" 
        sx={{ 
          ml: 1,
          opacity: 0.6,
          transition: 'opacity 0.3s ease-in-out',
          '&:hover': { opacity: 1 }
        }} 
      />;
    }
  };

  // Couleurs d'accordéon selon le statut avec couleurs de marque Krillou
  const getSectionColors = (section) => {
    const isValid = getSectionStatus(section);
    const hasData = section === 'basicInfo' ? 
      (formData.title || formData.description || formData.categoryId) :
      section === 'address' ? 
      (formData.country_code || formData.city || formData.address) :
      section === 'pricing' ?
      (formData.priceHourly || formData.priceDaily || formData.priceWeekly || formData.priceMonthly || formData.depositAmount) :
      section === 'images' ?
      formData.images.length > 0 :
      section === 'availability' ?
      (isEditMode && id && availabilityPeriods.length > 0) : false;

    // Couleurs de marque Krillou
    const brandColors = {
      primary: '#6200ee',      // Violet principal
      primaryLight: '#8e3dff', // Violet clair  
      primaryDark: '#4b00b5',  // Violet foncé
      secondary: '#03dac4',    // Turquoise
      secondaryLight: '#33ddc0', // Turquoise clair
      secondaryDark: '#00a896'  // Turquoise foncé
    };

    if (isValid) {
      return {
        borderColor: brandColors.secondary,
        backgroundColor: '#e8f5f3',
        headerBg: `linear-gradient(45deg, ${brandColors.secondary} 30%, ${brandColors.secondaryLight} 90%)`,
        headerColor: '#ffffff',
        hoverBg: `linear-gradient(45deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`
      };
    } else if (hasData) {
      return {
        borderColor: brandColors.primary,
        backgroundColor: '#f3e5f5',
        headerBg: `linear-gradient(45deg, ${brandColors.primary} 30%, ${brandColors.primaryLight} 90%)`,
        headerColor: '#ffffff',
        hoverBg: `linear-gradient(45deg, ${brandColors.secondary} 0%, ${brandColors.primary} 100%)`
      };
    } else {
      return {
        borderColor: '#e0e0e0',
        backgroundColor: '#fafafa',
        headerBg: 'linear-gradient(45deg, #9e9e9e 30%, #bdbdbd 90%)',
        headerColor: '#ffffff',
        hoverBg: `linear-gradient(45deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`
      };
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateDetailsSection = useCallback((options = {}) => {
    if (!formData.categoryId || !formData.offer_type) {
      return { isValid: true, errors: {}, firstErrorKey: null, reason: null };
    }

    const imperativeValidation = listingDetailsEditorRef.current?.validateAllFields?.({
      shouldScroll: options.shouldScroll
    });

    const validationResult = imperativeValidation || validateListingDetailsValues({
      fieldDefs: listingDetailsDefinitionsRef.current,
      details: formData.details,
      t,
      loading: listingDetailsStatusRef.current.loading,
      fetchError: listingDetailsStatusRef.current.fetchError
    });

    if (!validationResult.isValid && options.shouldScroll !== false) {
      setExpandedSections(prev => ({
        ...prev,
        basicInfo: true
      }));

      setTimeout(() => {
        listingDetailsEditorRef.current?.scrollToFirstError?.(validationResult.firstErrorKey);
      }, 180);
    }

    return validationResult;
  }, [formData.categoryId, formData.offer_type, formData.details, t]);

  // Mettre à jour handleInputChange pour gérer le pays
  const handleCountryChange = async (event) => {
    const code = event.target.value;
    const selectedCountry = countries.find(c => c.code === code);
    
    // Récupération dynamique de la devise via notre utilitaire
    const currencyInfo = await getCurrencyForCountry(code);
    
    // Stocker les informations complètes de la devise
    setCurrentCurrencyInfo(currencyInfo);

    // En mode édition : valider sans modifier la devise
    if (isEditMode && originalCurrencyRef.current) {
      setFormData(prev => ({
        ...prev,
        country_code: code,
        country: selectedCountry?.name || '',
        city: ''
      }));
      if (currencyInfo.code !== originalCurrencyRef.current) {
        setErrors(prev => ({
          ...prev,
          country_code: t('listing.errors.currencyMismatch', {
            from: originalCurrencyRef.current,
            to: currencyInfo.code
          })
        }));
      } else {
        setErrors(prev => {
          const newErr = { ...prev };
          delete newErr.country_code;
          return newErr;
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        country_code: code,
        country: selectedCountry?.name || '',
        currency: currencyInfo.code,
        city: ''
      }));
    }
    setCurrencyUpdateKey(prev => prev + 1);
    setCityOptions([]);
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024
    );

    if (validFiles.length !== files.length) {
      showNotification(t('listing.form.imagesRejected'), 'warning');
    }

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...validFiles.map(file => ({ file, preview: URL.createObjectURL(file) }))]
    }));
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const image = prev.images[index];
      if (image?.preview?.startsWith('blob:')) URL.revokeObjectURL(image.preview);
      return { ...prev, images: prev.images.filter((_, i) => i !== index) };
    });
  };

  /**
   * Définir l'image à targetIndex comme photo principale (index 0).
   * En mode création, le backend assigne order_index selon l'ordre des fichiers reçus.
   * En mode édition, on envoie imageOrder pour réordonner côté backend.
   */
  const handleSetMainImage = (targetIndex) => {
    if (targetIndex === 0) return;
    setFormData(prev => {
      const newImages = [...prev.images];
      [newImages[0], newImages[targetIndex]] = [newImages[targetIndex], newImages[0]];
      return { ...prev, images: newImages };
    });
  };
  
  // Gestionnaires d'événements pour les disponibilités
  const handleLoadAvailability = useCallback(async () => {
    // Ne rien charger si nous sommes en mode création (pas d'id)
    if (!isEditMode || !id) {
      console.log(`🚫 [CreateListingForm] Not loading availability: isEditMode=${isEditMode}, id=${id}`);
      return;
    }
    
    try {
      console.log(`🔍 [CreateListingForm] Loading availability for listing ID: ${id}`);
      const data = await fetchAvailability(id);
      console.log(`📊 [CreateListingForm] Availability data received:`, data);
      setAvailabilityPeriods(data || []);
      
      // S'assurer que l'accordéon reste ouvert après le chargement initial
      setTimeout(() => {
        setExpandedSections(prev => ({
          ...prev,
          availability: true
        }));
      }, 100);
      
    } catch (error) {
      console.error('❌ [CreateListingForm] Failed to load availability data:', error);
    }
  }, [isEditMode, id]); // Seules les vraies dépendances

  // Chargement initial UNIQUE des disponibilités existantes
  useEffect(() => {
    if (isEditMode && id && availabilityPeriods.length === 0) {
      console.log('🔍 CHARGEMENT INITIAL des disponibilités pour le listing:', id);
      
      // Chargement initial depuis le backend
      (async () => {
        try {
          const data = await fetchAvailability(id);
          console.log('📊 Données chargées avec succès:', data);
          console.log('🔍 IDs des périodes chargées:', data?.map(p => ({ id: p.id, type: typeof p.id })));
          setAvailabilityPeriods(data || []);
          
          // Forcer l'accordéon ouvert
          setExpandedSections(prev => ({
            ...prev,
            availability: true
          }));
        } catch (error) {
          console.error('❌ Erreur chargement initial:', error);
          showNotification(t('listing.errors.fetchFailed'), 'error');
        }
      })();
    }
  }, [isEditMode, id]);

  // Protection contre la fermeture/navigation pendant la soumission
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (submitting) {
        e.preventDefault();
        const message = t('listing.overlay.confirmLeave');
        e.returnValue = message;
        return message;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [submitting, t]);

  // Scroll automatique vers le haut de la page quand l'overlay s'active
  useEffect(() => {
    if (submitting) {
      // Scroll immédiat vers le haut
      window.scrollTo(0, 0);
      
      // Assurer que le body et html scrollent aussi
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [submitting]);

  // Rafraîchir le calendrier de disponibilité
  const refreshAvailabilityCalendar = () => {
    setAvailabilityRefreshKey(Date.now());
  };
  
  // Gérer la sélection d'une période sur le calendrier
  const handleSelectAvailabilityPeriod = (periodData) => {
    setSelectedPeriod(periodData);
    setAvailabilityFormMode(periodData.isNew ? 'add' : 'edit');
    setIsFormModalOpen(true);
  };
  
  // Gérer l'ajout d'une nouvelle période
  const handleAddAvailabilityPeriod = () => {
    setSelectedPeriod({
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // +7 jours
      status: AVAILABILITY_STATUS.AVAILABLE,
      reason: ''
    });
    setAvailabilityFormMode('add');
    setIsFormModalOpen(true);
  };
  
  // Gérer le blocage d'une période
  const handleBlockPeriod = () => {
    setSelectedPeriod({
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // +7 jours
      status: AVAILABILITY_STATUS.BLOCKED,
      reason: ''
    });
    setAvailabilityFormMode('block');
    setIsFormModalOpen(true);
  };
  
  // Gérer la sauvegarde d'une période - VERSION AVEC UTILITAIRE CENTRALISÉ
  const handleSavePeriod = async (formData) => {
    try {
      console.log('🔄 handleSavePeriod - Mode avec utilitaire centralisé:', formData);

      const existingPeriod = formData?.id
        ? availabilityPeriods.find(period => String(period.id) === String(formData.id))
        : null;

      if (existingPeriod && isImmutableAvailabilityPeriod(existingPeriod)) {
        showNotification(t('availabilityManager:immutability.reservation', 'Cette période ne peut pas être modifiée manuellement'), 'warning');
        return;
      }
      
      debugDates("HandleSavePeriod Input", formData);
      
      // FORCER l'accordéon ouvert immédiatement
      keepAvailabilityExpandedRef.current = true;
      setExpandedSections(prev => ({ ...prev, availability: true }));
      
      // UTILISER L'UTILITAIRE CENTRALISÉ POUR NORMALISER LES DATES
      const startDateFormatted = formatToDateString(formData.available_from || formData.startDate || formData.start_date);
      const endDateFormatted = formatToDateString(formData.available_to || formData.endDate || formData.end_date);
      
      // Vérifier la validité de la plage de dates
      if (!isValidDateRange(startDateFormatted, endDateFormatted)) {
        throw new Error(t('listing.availability.invalidDateRange'));
      }
      
      // Créer la période en local avec des dates normalisées
      const newPeriod = {
        id: formData.id || `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        available_from: startDateFormatted,
        available_to: endDateFormatted,
        start_date: new Date(startDateFormatted + 'T00:00:00'),
        end_date: new Date(endDateFormatted + 'T00:00:00'),
        status: formData.status,
        reason: formData.reason || '',
        price_per_night: formData.price_per_night || 0,
        isLocal: true
      };
      
      debugDates("HandleSavePeriod New Period", newPeriod);
      
      if (availabilityFormMode === 'edit' && formData.id) {
        // MODIFICATION - Marquer la période comme modifiée si elle n'est pas locale
        if (!formData.id.toString().startsWith('local-') && !formData.id.toString().startsWith('temp-')) {
          setModifiedAvailabilityIds(prev => {
            if (!prev.includes(formData.id)) {
              return [...prev, formData.id];
            }
            return prev;
          });
          console.log('🔄 Période marquée pour modification:', formData.id);
        }
        
        setAvailabilityPeriods(prev => 
          prev.map(period => String(period.id) === String(formData.id) ? newPeriod : period)
        );
        showNotification(t('listing.availability.periodUpdatedNormalized'), 'success');
      } else {
        // AJOUT
        setAvailabilityPeriods(prev => [...prev, newPeriod]);
        showNotification(t('listing.availability.periodAddedNormalized'), 'success');
      }
      
      // Fermer le modal
      setIsFormModalOpen(false);
      
      // FORCER l'accordéon ouvert après un délai
      setTimeout(() => {
        setExpandedSections(prev => ({ ...prev, availability: true }));
      }, 100);
      
      console.log('✅ Période sauvegardée localement sans problème');
      
    } catch (error) {
      console.error('Error saving period:', error);
      showNotification(t('listing.errors.savePeriodFailed'), 'error');
    }
  };
  
  // Supprimer une période - VERSION SIMPLE
  const handleDeletePeriod = async (periodId) => {
    try {
      console.log('🗑️ Suppression simple:', periodId);
      console.log('🔍 Type et valeur de periodId:', typeof periodId, periodId);

      const existingPeriod = availabilityPeriods.find(period => String(period.id) === String(periodId));
      if (existingPeriod && isImmutableAvailabilityPeriod(existingPeriod)) {
        showNotification(
          existingPeriod.reservation_id || existingPeriod.reservation?.id
            ? t('availabilityManager:immutability.linkedReservation', 'Cette période est liée à une réservation et sera libérée uniquement après annulation')
            : t('availabilityManager:immutability.past', 'Les périodes passées sont verrouillées'),
          'warning'
        );
        return;
      }
      
      // Mémoriser la plage de dates AVANT suppression (pour le nettoyage du calendrier)
      const periodToRemove = availabilityPeriods.find(period => String(period.id) === String(periodId));
      if (periodToRemove) {
        const fromStr = periodToRemove.available_from || periodToRemove.start_date;
        const toStr = periodToRemove.available_to || periodToRemove.end_date;
        if (fromStr && toStr) {
          setDeletedPeriodDateRanges(prev => [...prev, { available_from: fromStr, available_to: toStr }]);
        }
      }

      // Si c'est une période existante (pas locale), l'ajouter à la liste des suppressions
      keepAvailabilityExpandedRef.current = true;
      if (!periodId.toString().startsWith('local-') && !periodId.toString().startsWith('temp-')) {
        console.log('📝 Période existante ajoutée à la liste des suppressions:', periodId);
        console.log('🔍 Validation - periodId numérique?', !isNaN(Number(periodId)));
        console.log('🔍 Validation - periodId > 0?', Number(periodId) > 0);
        
        // Vérifier que l'ID est valide avant de l'ajouter
        const numericId = Number(periodId);
        if (!isNaN(numericId) && numericId > 0) {
          setDeletedAvailabilityIds(prev => [...prev, numericId]);
        } else {
          console.warn('⚠️ ID de période invalide, suppression ignorée:', periodId);
        }
      } else {
        console.log('📝 Période locale, pas de suppression backend nécessaire');
      }
      
      // SUPPRIMER de l'état local
      setAvailabilityPeriods(prev => prev.filter(p => String(p.id) !== String(periodId)));
      
      // FERMER le modal
      setIsFormModalOpen(false);
      
      // FORCER l'accordéon ouvert
      setTimeout(() => {
        setExpandedSections(prev => ({ ...prev, availability: true }));
      }, 100);
      
      showNotification(t('listing.availability.periodDeleted'), 'success');
      
    } catch (error) {
      console.error('Error deleting period:', error);
      showNotification(t('listing.errors.deletePeriodFailed'), 'error');
    }
  };

  const handleSubmit = async () => {
    // Valider toutes les sections
    const basicErrors = validateBasicInfo();
    const addressErrors = validateAddress();
    const pricingErrors = validatePricing();
    const availabilityErrors = validateAvailability();
    const detailsValidation = validateDetailsSection({ shouldScroll: false });
    const bookingValidation = validateBookingSettingsConsistency();
    setBookingSettingsFieldErrors(bookingValidation.fieldErrors);
    setBookingSettingsGlobalErrors(bookingValidation.globalErrors);
    
    const allErrors = { ...basicErrors, ...addressErrors, ...pricingErrors, ...availabilityErrors, ...bookingValidation.fieldErrors };
    setErrors(allErrors);
    
    // Debug: afficher les erreurs détaillées
    console.log('=== VALIDATION DEBUG ===');
    console.log('Form Data:', formData);
    console.log('Basic Errors:', basicErrors);
    console.log('Address Errors:', addressErrors);
    console.log('Pricing Errors:', pricingErrors);
    console.log('All Errors:', allErrors);
    console.log('🔍 COORDONNÉES ÉTAT FINAL:');
    console.log('  formData.latitude:', formData.latitude, '(type:', typeof formData.latitude, ')');
    console.log('  formData.longitude:', formData.longitude, '(type:', typeof formData.longitude, ')');
    console.log('  formData.place_id:', formData.place_id, '(type:', typeof formData.place_id, ')');
    console.log('  Sera envoyé au backend?', {
      latitude: formData.latitude !== null && formData.latitude !== undefined && formData.latitude !== '',
      longitude: formData.longitude !== null && formData.longitude !== undefined && formData.longitude !== '',
      place_id: formData.place_id !== null && formData.place_id !== undefined && formData.place_id !== ''
    });
    console.log('Coordinates check:', {
      latitude: formData.latitude,
      longitude: formData.longitude,
      latType: typeof formData.latitude,
      lonType: typeof formData.longitude,
      latNumber: Number(formData.latitude),
      lonNumber: Number(formData.longitude),
      latValid: !isNaN(Number(formData.latitude)),
      lonValid: !isNaN(Number(formData.longitude))
    });
    console.log('========================');
    
    if (Object.keys(allErrors).length > 0 || !bookingValidation.isValid || !detailsValidation.isValid) {
      if (!detailsValidation.isValid) {
        setWizardStep(0);
        setExpandedSections(prev => ({
          ...prev,
          basicInfo: true
        }));
        showNotification(
          detailsValidation.message || t('listing:details.errors.formInvalid', 'Veuillez corriger les erreurs ci-dessous.'),
          'error'
        );
        setTimeout(() => {
          listingDetailsEditorRef.current?.scrollToFirstError?.(detailsValidation.firstErrorKey);
        }, 550);
        return;
      }

      if (!bookingValidation.isValid) {
        setWizardStep(2);
      }
      showNotification(t('common.validation.fixFormErrors'), 'error');
      scrollToFirstError(allErrors);
      return;
    }

    // Activer l'overlay de soumission AVANT tout traitement
    setSubmitting(true);
    setSubmissionMessage(isEditMode ? t('listing.overlay.updating') : t('listing.overlay.creating'));
    
    // Garantir que l'état de l'overlay est mis à jour avant de continuer
    await new Promise(resolve => setTimeout(resolve, 0));
    
    // Enregistrer le temps de début pour garantir un temps minimum d'affichage
    const startTime = Date.now();

    setLoading(true);
    try {
      console.log('=== FORM SUBMISSION DEBUG ===');
      console.log('Mode:', isEditMode ? 'EDIT' : 'CREATE');
      console.log('Listing ID:', id);
      console.log('Form Data avant envoi:', formData);
      
      // Validation supplémentaire pour éviter les erreurs backend
      if (!formData.city || formData.city.trim() === '') {
        showNotification(t('listing.errors.cityRequired'), 'error');
        setLoading(false);
        return;
      }
      
      const formDataToSend = new FormData();
      // Correction : conversion des champs de prix en snake_case pour le backend
      const priceFieldMap = {
        priceHourly: 'price_hourly',
        priceDaily: 'price_daily',
        priceWeekly: 'price_weekly',
        priceMonthly: 'price_monthly',
        pricePerSlot: 'price_per_slot',
        depositAmount: 'deposit_amount'
      };
      
      Object.keys(formData).forEach(key => {
        if (key !== 'images') {
          const backendKey = priceFieldMap[key] || key;
          const value = formData[key];
          
          // LOGS SPÉCIAUX POUR LES COORDONNÉES
          if (key === 'latitude' || key === 'longitude' || key === 'place_id') {
            console.log(`🎯 COORDONNÉE ${key.toUpperCase()}:`, {
              key: key,
              backendKey: backendKey,
              value: value,
              type: typeof value,
              isNull: value === null,
              isUndefined: value === undefined,
              isEmpty: value === '',
              willBeSent: value !== null && value !== undefined && value !== ''
            });
          }
          
          // Validation spéciale pour les champs critiques
          if (key === 'city' && (!value || value.trim() === '')) {
            console.warn('City field is empty or invalid:', value);
            return; // Skip this field
          }
          
          // Ne pas envoyer les valeurs null ou undefined
          if (value !== null && value !== undefined && value !== '') {
            // Sérialiser les objets en JSON avant d'appendre
            const serializedValue = typeof value === 'object' ? JSON.stringify(value) : value;
            formDataToSend.append(backendKey, serializedValue);
            console.log(`Added field: ${backendKey} = ${serializedValue}`);
            
            // LOG SPÉCIAL pour confirmer l'envoi des coordonnées
            if (key === 'latitude' || key === 'longitude' || key === 'place_id') {
              console.log(`✅ COORDONNÉE ${key.toUpperCase()} AJOUTÉE AU FORMDATA !`);
            }
          } else {
            // LOG SPÉCIAL pour les coordonnées rejetées
            if (key === 'latitude' || key === 'longitude' || key === 'place_id') {
              console.log(`❌ COORDONNÉE ${key.toUpperCase()} REJETÉE (null/undefined/empty)`);
            }
          }
        }
      });
      
      // Ajouter booking_settings (JSONB) - toujours envoyer s'il contient des données
      // Sanitisation selon le booking_mode pour éviter les champs orphelins
      const rawBookingSettings = { ...bookingSettings };
      const currentBookingMode = formData.booking_mode;
      if (currentBookingMode === 'time_slot' || currentBookingMode === 'time_slot_capacity') {
        // Les créneaux n'utilisent pas date_range_mode
        delete rawBookingSettings.date_range_mode;
      } else if (currentBookingMode === 'date_range') {
        // Le mode par date n'utilise pas les champs créneaux
        delete rawBookingSettings.slot_duration;
        delete rawBookingSettings.opening_hours;
        delete rawBookingSettings.buffer_before;
        delete rawBookingSettings.buffer_after;
      }

      const hasBookingSettings = 
        rawBookingSettings.unit || 
        rawBookingSettings.min_duration || 
        rawBookingSettings.max_duration ||
        rawBookingSettings.slot_duration || 
        (rawBookingSettings.opening_hours && rawBookingSettings.opening_hours.length > 0) ||
        rawBookingSettings.buffer_before > 0 || 
        rawBookingSettings.buffer_after > 0 ||
        rawBookingSettings.pricing_mode || 
        rawBookingSettings.capacity_mode;
      
      if (hasBookingSettings) {
        formDataToSend.append('booking_settings', JSON.stringify(rawBookingSettings));
        console.log('✅ Added booking_settings:', rawBookingSettings);
      } else {
        console.log('⚠️ No booking_settings to send');
      }

      // Add images
      if (formData.images.length > 0) {
        setSubmissionMessage(t('listing.overlay.preparingImages'));
      }
      
      if (isEditMode) {
        // En mode édition, gérer les images existantes et nouvelles séparément
        const existingImages = [];
        const newImages = [];
        
        formData.images.forEach((image, index) => {
          const file = image instanceof File ? image : image.file;
          if (file instanceof File) {
            newImages.push(file);
            console.log(`New image ${index}:`, file.name);
          } else {
            // Image existante - conserver son ID/URL
            existingImages.push(image);
            console.log(`Existing image ${index}:`, {
              id: image.id,
              url: image.url,
              fullObject: image
            });
          }
        });
        
        // Ajouter les nouvelles images au FormData
        newImages.forEach(image => {
          formDataToSend.append('images', image);
        });
        
        // Ajouter les IDs des images existantes à conserver
        if (existingImages.length > 0) {
          const existingImageIds = existingImages.map(img => img.id).filter(Boolean);
          if (existingImageIds.length > 0) {
            formDataToSend.append('existingImages', JSON.stringify(existingImageIds));
            console.log('Existing images to keep (IDs):', existingImageIds);
          }
          // Envoyer l'ordre souhaité des images existantes (permet de changer la photo principale)
          formDataToSend.append('imageOrder', JSON.stringify(existingImageIds));
          console.log('Image order (for main image selection):', existingImageIds);
        } else {
          // Aucune image existante à conserver - envoyer un tableau vide pour supprimer toutes les images
          formDataToSend.append('existingImages', JSON.stringify([]));
          console.log('No existing images to keep - will delete all existing images');
        }
      } else {
        // En mode création, ajouter toutes les images (qui sont des files)
        formData.images.forEach((image, index) => {
          const file = image instanceof File ? image : image.file;
          if (file instanceof File) {
            formDataToSend.append('images', file);
            console.log(`Added new image ${index}:`, file.name);
          }
        });
      }
      
      // Log du contenu FormData
      console.log('=== FORMDATA CONTENTS DEBUG ===');
      for (let [key, value] of formDataToSend.entries()) {
        if (value instanceof File) {
          console.log(`${key}: [File] ${value.name} (${value.size} bytes)`);
        } else {
          console.log(`${key}: ${value}`);
        }
      }
      console.log('=== END FORMDATA DEBUG ===');
      
      // Mettre à jour le message de l'overlay
      setSubmissionMessage(isEditMode ? t('listing.overlay.saving') : t('listing.overlay.sending'));
      
      // Ajouter les données de disponibilité si nous avons des périodes définies
      console.log('=== CHECKING AVAILABILITY CONDITIONS ===');
      console.log('isEditMode:', isEditMode);
      console.log('availabilityPeriods:', availabilityPeriods);
      console.log('availabilityPeriods.length:', availabilityPeriods.length);
      console.log('Condition (!isEditMode && availabilityPeriods.length > 0):', (!isEditMode && availabilityPeriods.length > 0));
      
      if (!isEditMode && availabilityPeriods.length > 0) {
        console.log('=== AVAILABILITY DATA AVEC UTILITAIRE CENTRALISÉ ===');
        console.log('availabilityPeriods state avant traitement:', availabilityPeriods);
        
        // Utiliser l'utilitaire centralisé pour normaliser toutes les périodes
        const availabilityData = availabilityPeriods.map(period => {
          const normalizedPeriod = preparePeriodForApi({
            available_from: period.available_from,
            available_to: period.available_to,
            status: period.status,
            reason: period.reason
          });
          
          debugDates(`Period ${period.id}`, normalizedPeriod);
          return normalizedPeriod;
        });
        
        console.log('availabilityData normalisées pour API:', availabilityData);
        console.log('JSON stringified:', JSON.stringify(availabilityData));
        formDataToSend.append('availability', JSON.stringify(availabilityData));
        console.log('✅ AVAILABILITY DATA NORMALISÉE AJOUTÉE AU FORMDATA');
        console.log('=== END AVAILABILITY DEBUG ===');
      } else {
        console.log('❌ AVAILABILITY DATA NOT ADDED - Conditions not met');
      }
      
      // Utiliser l'instance api (axios) pour POST ou PUT selon le mode
      let response;
      if (isEditMode) {
        console.log(`Making PUT request to /listings/${id}`);
        response = await api.put(`/listings/${id}`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        console.log('Making POST request to /listings');
        response = await api.post('/listings', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      const result = response.data;
      console.log('=== API RESPONSE DEBUG ===');
      console.log('Full response status:', response.status);
      console.log('Full response data:', result);
      console.log('Success:', result.success);
      console.log('Message:', result.message);
      console.log('Listing object:', result.listing);
      console.log('Listing ID:', result.listing?.id);
      console.log('========================');
      
      showNotification(
        isEditMode ? t('listing.success.updated') : t('listing.success.created'), 
        'success'
      );
      
      // Récupérer l'ID du listing (créé ou modifié)
      const listingId = result.listing?.id || id;
      
      // Si nous avons des périodes de disponibilité à traiter
      if (availabilityPeriods.length > 0 && listingId || deletedAvailabilityIds.length > 0 || modifiedAvailabilityIds.length > 0) {
        setSubmissionMessage(t('listing.overlay.savingAvailability'));
        console.log('💾 Traitement des disponibilités dans le bon ordre...');
        console.log('📊 Statistiques:', {
          totalPeriods: availabilityPeriods.length,
          deletedIds: deletedAvailabilityIds.length,
          modifiedIds: modifiedAvailabilityIds.length
        });
        
        // === ÉTAPE 1: SUPPRIMER LES PÉRIODES MARQUÉES POUR SUPPRESSION ===
        if (deletedAvailabilityIds.length > 0 && listingId) {
          console.log('🗑️ ÉTAPE 1: Suppression des disponibilités:', deletedAvailabilityIds);
          
          try {
            for (const periodId of deletedAvailabilityIds) {
              console.log('📤 Tentative de suppression de la période:', periodId, 'Type:', typeof periodId);
              
              // Validation supplémentaire avant appel API
              const numericId = Number(periodId);
              if (isNaN(numericId) || numericId <= 0) {
                console.error('❌ ID invalide ignoré:', periodId);
                continue;
              }
              
              try {
                // Utiliser l'API de suppression directe
                await deleteAvailabilityPeriod(numericId);
                console.log('✅ Période supprimée avec succès:', periodId);
              } catch (deleteError) {
                console.error('❌ Erreur lors de la suppression de la période:', periodId, deleteError);
                // Continuer avec les autres suppressions même en cas d'erreur
              }
            }
            console.log('✅ Toutes les suppressions traitées');
          } catch (deleteError) {
            console.error('❌ Erreur globale lors des suppressions:', deleteError);
            showNotification(t('listing.availability.someDeletesFailed'), 'warning');
          }
        }
        
        // === ÉTAPE 2: METTRE À JOUR LES PÉRIODES MODIFIÉES ===
        if (modifiedAvailabilityIds.length > 0 && listingId) {
          console.log('🔄 ÉTAPE 2: Mise à jour des périodes modifiées:', modifiedAvailabilityIds);
          
          try {
            for (const periodId of modifiedAvailabilityIds) {
              // Trouver la période modifiée dans availabilityPeriods
              const modifiedPeriod = availabilityPeriods.find(p => String(p.id) === String(periodId));
              
              if (modifiedPeriod) {
                console.log('📤 Mise à jour de la période:', periodId, modifiedPeriod);
                
                // UTILISER L'UTILITAIRE CENTRALISÉ pour normaliser les données
                const updateData = preparePeriodForApi({
                  available_from: modifiedPeriod.available_from,
                  available_to: modifiedPeriod.available_to,
                  status: modifiedPeriod.status,
                  reason: modifiedPeriod.reason
                });
                
                debugDates(`Update Period ${periodId}`, updateData);
                
                try {
                  await updateAvailabilityPeriod(periodId, updateData);
                  console.log('✅ Période mise à jour avec succès:', periodId);
                } catch (updateError) {
                  console.error('❌ Erreur lors de la mise à jour de la période:', periodId, updateError);
                  // Continuer avec les autres modifications même en cas d'erreur
                }
              }
            }
            console.log('✅ Toutes les modifications traitées');
          } catch (updateError) {
            console.error('❌ Erreur globale lors des modifications:', updateError);
            showNotification(t('listing.availability.someUpdatesFailed'), 'warning');
          }
        }
        
        // === ÉTAPE 3: CRÉER LES NOUVELLES PÉRIODES ===
        const newPeriods = availabilityPeriods.filter(period => 
          period.isLocal || (period.id && (
            period.id.toString().startsWith('temp-') || 
            period.id.toString().startsWith('local-')
          ))
        );
        
        if (newPeriods.length > 0 && listingId) {
          console.log('➕ ÉTAPE 3: Création des nouvelles périodes:', newPeriods.length);
          
          try {
            for (const period of newPeriods) {
              console.log('� Création de la nouvelle période:', period);
              
              // UTILISER L'UTILITAIRE CENTRALISÉ pour normaliser les données
              const createData = preparePeriodForApi({
                available_from: period.available_from,
                available_to: period.available_to,
                status: period.status,
                reason: period.reason
              });
              
              debugDates(`Create New Period ${period.id}`, createData);
              
              try {
                await addAvailabilityPeriod(
                  listingId,
                  createData.available_from,
                  createData.available_to,
                  createData.status,
                  createData.reason
                );
                console.log('✅ Nouvelle période créée avec succès:', period.id);
              } catch (createError) {
                console.error('❌ Erreur lors de la création de la période:', period.id, createError);
                // Continuer avec les autres créations même en cas d'erreur
              }
            }
            console.log('✅ Toutes les nouvelles périodes créées');
          } catch (createError) {
            console.error('❌ Erreur globale lors des créations:', createError);
            showNotification(t('listing.availability.someCreatesFailed'), 'warning');
          }
        }
        
        console.log('✅ Traitement complet des disponibilités terminé');
      }
      
      // Garantir un temps minimum d'affichage de l'overlay
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_OVERLAY_DISPLAY_TIME - elapsed);
      
      if (remaining > 0) {
        setSubmissionMessage(t('listing.overlay.finalizing'));
        await new Promise(resolve => setTimeout(resolve, remaining));
      }
      
      // En mode édition, recharger les données fraîches et rediriger
      if (isEditMode) {
        console.log('Edit mode: Reloading fresh data after successful update...');
        
        // Réinitialiser les trackers de modifications après sauvegarde réussie
        setModifiedAvailabilityIds([]);
        setDeletedAvailabilityIds([]);
        
        // Recharger les données fraîches après modification
        try {
          const timestamp = new Date().getTime();
          const freshResponse = await api.get(`/listings/${id}?_t=${timestamp}`);
          console.log('Fresh data loaded after edit:', freshResponse.data);
          
          // Désactiver l'overlay avant la redirection
          setSubmitting(false);
          
          // Rediriger vers la page de détail
          navigate(`/listing/${id}`, { 
            replace: true,
            state: { refreshed: true }
          });
        } catch (fetchError) {
          console.error('Error fetching fresh data:', fetchError);
          // Si erreur, rediriger quand même vers la page de détail
          setSubmitting(false);
          navigate(`/listing/${id}`, { replace: true });
        }
        return;
      }
      
      // Mode création: redirection normale
      setSubmitting(false);
      
      if (result.success && result.listing && result.listing.id) {
        const listingId = result.listing.id;
        console.log('Redirecting to listing detail page:', `/listing/${listingId}`);
        navigate(`/listing/${listingId}`);
      } else {
        console.error('Structure de réponse inattendue:', result);
        console.error('Redirection vers la page des annonces...');
        navigate('/listings');
      }
    } catch (error) {
      console.error('=== ERROR DEBUG ===');
      console.error('Error creating/updating listing:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Error config:', error.config);
      
      // Log additionnel pour diagnostiquer l'erreur
      if (error.response?.data) {
        console.error('Backend error details:', {
          success: error.response.data.success,
          message: error.response.data.message || error.response.data.msg,
          errorKey: error.response.data.errorKey,
          originalError: error.response.data.originalError
        });
      }
      
      // Garantir le temps minimum d'affichage même en cas d'erreur
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_OVERLAY_DISPLAY_TIME - elapsed);
      
      if (remaining > 0) {
        await new Promise(resolve => setTimeout(resolve, remaining));
      }
      
      const backendMessage = error?.response?.data?.message || error?.response?.data?.msg || '';
      if (typeof backendMessage === 'string' && backendMessage.toLowerCase().includes('booking_settings')) {
        setBookingSettingsGlobalErrors([backendMessage]);
        setWizardStep(2);
      }

      // Utiliser getErrorMessage pour obtenir un message internationalisé
      const errorMessage = getErrorMessage(error, t);
      showNotification(errorMessage, 'error');
      
      // Désactiver l'overlay après l'erreur
      setSubmitting(false);
      console.error('==================');
    } finally {
      setLoading(false);
    }
  };

  // FORMULAIRE AVEC ACCORDÉONS
  
  // Si nous chargeons les données initiales
  if (initialLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box ref={formContainerRef} sx={{ maxWidth: 880, mx: 'auto', p: 3, position: 'relative' }}>
      {/* Overlay de chargement pendant la soumission */}
      {submitting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 }
            }
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 3,
              padding: 5,
              boxShadow: '0 8px 40px rgba(98, 0, 238, 0.3)',
              textAlign: 'center',
              minWidth: 350,
              maxWidth: 500,
              border: '2px solid #6200ee'
            }}
          >
            <CircularProgress 
              size={70} 
              thickness={4} 
              sx={{
                color: '#6200ee',
                mb: 3
              }}
            />
            
            <Box mt={3}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700,
                  mb: 2
                }}
              >
                {submissionMessage}
              </Typography>
              
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 3, mt: 2, fontSize: '0.95rem' }}
              >
                {t('listing.overlay.pleaseWait')}
              </Typography>
              
              <LinearProgress 
                sx={{ 
                  width: '100%',
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(98, 0, 238, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                    borderRadius: 4
                  }
                }} 
              />
              
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  mt: 3, 
                  display: 'block', 
                  fontStyle: 'italic',
                  fontSize: '0.85rem'
                }}
              >
                ⚠️ {t('listing.overlay.doNotClose')}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Bouton Retour */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{
            borderColor: '#6200ee',
            color: '#6200ee',
            fontWeight: 'medium',
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: '0 2px 4px rgba(98, 0, 238, 0.1)',
            '&:hover': {
              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
              color: 'white',
              borderColor: 'transparent',
              boxShadow: '0 4px 8px rgba(98, 0, 238, 0.3)',
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {t('common.goBack')}
        </Button>
      </Box>
      
      <Box sx={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                    color: 'white',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}>
      <Typography variant="h4" gutterBottom>
        {isEditMode ? t('listing.edit.title') : t('listing.create.title')}
      </Typography>
      
      {/* Badge de statut en mode édition */}
      {isEditMode && existingListing?.status && (
        <Chip
          label={t(`listing.status.${existingListing.status}`)}
          icon={
            existingListing.status === 'active' ? <CheckCircleIcon /> :
            existingListing.status === 'pending_verification' ? <InfoIcon /> :
            existingListing.status === 'rejected' ? <ErrorIcon /> :
            existingListing.status === 'inactive' ? <BlockIcon /> :
            <InfoIcon />
          }
          sx={{
            fontSize: '1rem',
            fontWeight: 'bold',
            height: 'auto',
            py: 1,
            px: 2,
            backgroundColor: 
              existingListing.status === 'active' ? '#4caf50' :
              existingListing.status === 'pending_verification' ? '#ff9800' :
              existingListing.status === 'rejected' ? '#f44336' :
              existingListing.status === 'inactive' ? '#616161' :
              '#9e9e9e',
            color: 'white',
            '& .MuiChip-icon': {
              color: 'white'
            },
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}
        />
      )}
      </Box>
      
      {/* Notification de statut (rejected/inactive) en mode édition */}
      {isEditMode && existingListing && (
        <ListingStatusNotification 
          listing={existingListing}
        />
      )}
      
      {/* ─── Wizard Progress Bar ─────────────────────────────────────────── */}
      <Box sx={{ mt: 3, mb: 1 }}>
        {/* Stepper / dots */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, mb: 2 }}>
          {WIZARD_STEPS.map((step, idx) => {
            const done = idx < wizardStep;
            const active = idx === wizardStep;
            return (
              <React.Fragment key={step.key}>
                <Box
                  onClick={() => { if (done) setWizardStep(idx); }}
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    cursor: done ? 'pointer' : 'default', minWidth: 80
                  }}
                >
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '0.9rem',
                    background: done
                      ? 'linear-gradient(135deg, #03dac4, #00a896)'
                      : active
                      ? 'linear-gradient(135deg, #6200ee, #9c27b0)'
                      : '#e0e0e0',
                    color: (done || active) ? '#fff' : '#999',
                    boxShadow: active ? '0 4px 14px rgba(98,0,238,0.4)' : 'none',
                    transition: 'all 0.3s ease',
                    border: active ? '2px solid #6200ee' : '2px solid transparent'
                  }}>
                    {done ? '✓' : idx + 1}
                  </Box>
                  <Typography variant="caption" sx={{
                    mt: 0.5, fontWeight: active ? 700 : 400,
                    color: active ? '#6200ee' : done ? '#03dac4' : '#999',
                    fontSize: '0.7rem', textAlign: 'center'
                  }}>
                    {step.label}
                  </Typography>
                </Box>
                {idx < WIZARD_STEPS.length - 1 && (
                  <Box sx={{
                    flex: 1, height: 3, mt: '-18px',
                    background: idx < wizardStep
                      ? 'linear-gradient(90deg, #03dac4, #6200ee)'
                      : '#e0e0e0',
                    transition: 'background 0.4s ease'
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </Box>
        <LinearProgress
          variant="determinate"
          value={((wizardStep) / (WIZARD_STEPS.length - 1)) * 100}
          sx={{
            height: 6, borderRadius: 3,
            backgroundColor: '#ede7f6',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #6200ee, #03dac4)',
              borderRadius: 3
            }
          }}
        />
      </Box>

      <Paper sx={{ p: 3, mt: 2 }}>
        {/* ─── Étape 0 : Ressource (infos générales + images) ─── */}
        {wizardStep === 0 && <>
        {/* Section Informations générales */}
        <Accordion 
          expanded={expandedSections.basicInfo} 
          onChange={handleAccordionChange('basicInfo')}
          sx={{ 
            mb: 2,
            border: `2px solid ${getSectionColors('basicInfo').borderColor}`,
            backgroundColor: getSectionColors('basicInfo').backgroundColor,
            borderRadius: '12px !important',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: expandedSections.basicInfo ? 'scale(1.02)' : 'scale(1)',
            boxShadow: expandedSections.basicInfo ? 
              '0 8px 32px rgba(98, 0, 238, 0.25)' : 
              '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.01)',
              boxShadow: '0 12px 40px rgba(3, 218, 196, 0.3)',
              borderColor: getSectionColors('basicInfo').hoverBg.includes('primary') ? '#6200ee' : '#03dac4'
            },
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ 
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              transform: expandedSections.basicInfo ? 'rotate(180deg)' : 'rotate(0deg)'
            }} />}
            aria-controls="basic-info-content"
            id="basicInfo-header"
            sx={{
              background: getSectionColors('basicInfo').headerBg,
              color: getSectionColors('basicInfo').headerColor,
              minHeight: '64px !important',
              '&.Mui-expanded': {
                minHeight: '64px !important'
              },
              '& .MuiAccordionSummary-content': {
                margin: '16px 0 !important',
                '&.Mui-expanded': {
                  margin: '16px 0 !important'
                }
              },
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                background: getSectionColors('basicInfo').hoverBg,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 20px rgba(98, 0, 238, 0.3)'
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center">
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: getSectionStatus('basicInfo') ? '#ffffff' : 'rgba(255,255,255,0.5)',
                  mr: 2,
                  animation: getSectionStatus('basicInfo') ? 'glow 2s infinite' : 'none',
                  '@keyframes glow': {
                    '0%, 100%': { boxShadow: '0 0 5px rgba(255,255,255,0.5)' },
                    '50%': { boxShadow: '0 0 15px rgba(255,255,255,1)' }
                  }
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('listing.form.sections.basicInfo') || 'Informations générales'}
                </Typography>
              </Box>
              {getSectionIcon('basicInfo')}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            p: 3,
            backgroundColor: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Grid container spacing={3}>
              {/* Titre */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('listing.form.title')}
                  value={formData.title}
                  onChange={e => handleInputChange('title', e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title}
                  required
                  inputRef={el => { fieldRefs.current.title = el; }}
                />
              </Grid>
              
              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label={t('listing.form.description')}
                  value={formData.description}
                  onChange={e => handleInputChange('description', e.target.value)}
                  error={!!errors.description}
                  helperText={errors.description}
                  required
                  inputRef={el => { fieldRefs.current.description = el; }}
                />
              </Grid>
              
              {/* Catégorie avec sélection hiérarchique améliorée */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {t('listing.form.category')}
                </Typography>
                
                {/* Autocomplete pour recherche rapide avec design amélioré */}
                <Autocomplete
                  fullWidth
                  options={filterCategoriesList(listingCategories.filter(isLeafCategory), categorySearchQuery)}
                  getOptionLabel={(option) => getCategoryFullPath(option.id)}
                  value={listingCategories.find(cat => cat.id === formData.categoryId) || null}
                  onChange={(_, value) => {
                    handleInputChange('categoryId', value ? value.id : '');
                    setCategorySearchQuery('');
                  }}
                  onInputChange={(_, newInputValue) => {
                    setCategorySearchQuery(newInputValue);
                  }}
                  loading={categoriesLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(98, 0, 238, 0.1)'
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(98, 0, 238, 0.2)',
                        borderColor: '#6200ee'
                      }
                    }
                  }}
                  renderInput={(params) => {
                    // Fusionner notre ref avec le inputRef interne de l'Autocomplete
                    const mergedInputRef = (el) => {
                      fieldRefs.current.categoryId = el;
                      if (params.inputRef) {
                        if (typeof params.inputRef === 'function') params.inputRef(el);
                        else params.inputRef.current = el;
                      }
                    };
                    return (
                      <TextField
                        {...params}
                        label={t('listing.form.categorySearch')}
                        placeholder={t('listing.form.categorySearchPlaceholder')}
                        error={!!errors.categoryId}
                        helperText={errors.categoryId}
                        required
                        inputRef={mergedInputRef}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {categoriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    );
                  }}
                  renderOption={(props, option) => (
                    (() => {
                      const optionImageUrl = getUploadedFileUrl(option.imageUrl);
                      return (
                    <li {...props} style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      margin: '2px 8px',
                      transition: 'all 0.2s ease-in-out',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                    }}>
                      {optionImageUrl ? (
                        <Avatar
                          variant="rounded"
                          src={optionImageUrl}
                          alt={getCategoryName(option)}
                          sx={{ width: 32, height: 32, borderRadius: '10px', mr: 2 }}
                          imgProps={{ loading: 'lazy' }}
                        />
                      ) : (
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}>
                          <InsertDriveFileIcon fontSize="small" sx={{ color: 'white' }} />
                        </Box>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {getCategoryName(option)}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          opacity: 0.8
                        }}>
                          {getCategoryFullPath(option.id)}
                        </Typography>
                      </Box>
                    </li>
                      );
                    })()
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText={t('listing.form.noCategoriesFound')}
                />

                {/* Affichage de la catégorie sélectionnée avec design moderne */}
                {formData.categoryId && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={getCategoryFullPath(formData.categoryId)}
                      color="primary"
                      variant="filled"
                      onDelete={() => handleInputChange('categoryId', '')}
                      sx={{ 
                        maxWidth: '100%',
                        height: 'auto',
                        p: 1,
                        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                        color: 'white',
                        fontWeight: 500,
                        borderRadius: '12px',
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-word'
                        },
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255,255,255,0.8)',
                          '&:hover': {
                            color: 'white'
                          }
                        }
                      }}
                    />

                    {(() => {
                      const selectedCategory = listingCategories.find(cat => String(cat.id) === String(formData.categoryId));
                      if (!selectedCategory) return null;
                      const selectedImageUrl = getUploadedFileUrl(selectedCategory.imageUrl);
                      const selectedTitle = getCategoryName(selectedCategory);
                      const selectedDescription = getCategoryDescription(selectedCategory);

                      return (
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            border: (theme) => `1px solid ${theme.palette.divider}`
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            {selectedImageUrl && (
                              <Box
                                component="img"
                                src={selectedImageUrl}
                                alt={selectedTitle}
                                sx={{
                                  width: { xs: '100%', sm: 220 },
                                  height: { xs: 160, sm: 140 },
                                  objectFit: 'cover',
                                  borderRadius: 2,
                                  backgroundColor: 'background.default'
                                }}
                                loading="lazy"
                              />
                            )}
                            <Box sx={{ flex: 1, minWidth: 240 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {selectedTitle}
                              </Typography>
                              {selectedDescription ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {selectedDescription}
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })()}
                  </Box>
                )}

                {/* Accordéon pour navigation hiérarchique */}
                <Box sx={{ mt: 3 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      border: '2px solid #e0e0e0',
                      borderRadius: '12px !important',
                      overflow: 'hidden',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                      '&:before': { display: 'none' },
                      '&:hover': {
                        boxShadow: '0 4px 20px rgba(98, 0, 238, 0.1)',
                        borderColor: '#6200ee'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ 
                        color: 'white',
                        transition: 'transform 0.3s ease-in-out'
                      }} />}
                      sx={{
                        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                        color: 'white',
                        minHeight: '56px !important',
                        '&.Mui-expanded': {
                          minHeight: '56px !important'
                        },
                        '&:hover': {
                          background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)',
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <FolderIcon sx={{ mr: 2 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {t('listing.form.orBrowseCategories') || 'Ou parcourir les catégories'}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ 
                      p: 0,
                      backgroundColor: '#fafafa'
                    }}>
                      <Box sx={{ 
                        maxHeight: 400, 
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                          width: '6px'
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                          borderRadius: '3px'
                        }
                      }}>
                        {categoriesLoading ? (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            p: 4,
                            flexDirection: 'column',
                            gap: 2
                          }}>
                            <CircularProgress 
                              size={32} 
                              sx={{ color: '#6200ee' }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {t('filters.categories.loadingCategories')}
                            </Typography>
                          </Box>
                        ) : categoryTreeRoots.length > 0 ? (
                          <List dense sx={{ p: 1 }}>
                            {renderCategoryTree(categoryTreeRoots)}
                          </List>
                        ) : (
                          <Box sx={{ 
                            p: 4, 
                            textAlign: 'center',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                          }}>
                            <FolderIcon sx={{ 
                              fontSize: 48, 
                              color: 'text.disabled',
                              mb: 2
                            }} />
                            <Typography variant="body2" color="text.secondary">
                              {t('listing.form.noCategoriesAvailable')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>

                {formData.categoryId && (
                  <ListingDetailsEditor
                    ref={listingDetailsEditorRef}
                    offerType={formData.offer_type}
                    value={formData.details}
                    onChange={(details) => {
                      setFormData(prev => ({
                        ...prev,
                        details
                      }));
                    }}
                    onDefinitionsChange={(definitions) => {
                      listingDetailsDefinitionsRef.current = definitions;
                    }}
                    onStatusChange={(status) => {
                      listingDetailsStatusRef.current = status;
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        </>}

        {/* ─── Étape 1 : Localisation ─── */}
        {wizardStep === 1 && <>
        {/* Section Adresse et Localisation */}
        <Accordion 
          expanded={expandedSections.address} 
          onChange={handleAccordionChange('address')}
          sx={{ 
            mb: 2,
            border: `2px solid ${getSectionColors('address').borderColor}`,
            backgroundColor: getSectionColors('address').backgroundColor,
            borderRadius: '12px !important',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: expandedSections.address ? 'scale(1.02)' : 'scale(1)',
            boxShadow: expandedSections.address ? 
              '0 8px 32px rgba(3, 218, 196, 0.25)' : 
              '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.01)',
              boxShadow: '0 12px 40px rgba(98, 0, 238, 0.3)',
              borderColor: getSectionColors('address').hoverBg.includes('primary') ? '#6200ee' : '#03dac4'
            },
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ 
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              transform: expandedSections.address ? 'rotate(180deg)' : 'rotate(0deg)'
            }} />}
            aria-controls="address-content"
            id="address-header"
            sx={{
              background: getSectionColors('address').headerBg,
              color: getSectionColors('address').headerColor,
              minHeight: '64px !important',
              '&.Mui-expanded': {
                minHeight: '64px !important'
              },
              '& .MuiAccordionSummary-content': {
                margin: '16px 0 !important',
                '&.Mui-expanded': {
                  margin: '16px 0 !important'
                }
              },
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                background: getSectionColors('address').hoverBg,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 20px rgba(3, 218, 196, 0.3)'
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center">
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: getSectionStatus('address') ? '#ffffff' : 'rgba(255,255,255,0.5)',
                  mr: 2,
                  animation: getSectionStatus('address') ? 'glow 2s infinite' : 'none',
                  '@keyframes glow': {
                    '0%, 100%': { boxShadow: '0 0 5px rgba(255,255,255,0.5)' },
                    '50%': { boxShadow: '0 0 15px rgba(255,255,255,1)' }
                  }
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('listing.form.sections.address') || 'Adresse et localisation'}
                </Typography>
              </Box>
              {getSectionIcon('address')}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            p: 3,
            backgroundColor: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Grid container spacing={3}>

              {/* Message informatif en mode édition : devise immuable */}
              {isEditMode && (
                <Grid item xs={12}>
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      border: '1.5px solid #0288d1',
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 100%)',
                      p: 2.5,
                      '& .MuiAlert-message': { width: '100%' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <InfoIcon sx={{ color: '#0277bd', fontSize: 28, mt: 0.2, flexShrink: 0 }} />
                      <Box>
                        <AlertTitle sx={{ color: '#01579b', fontWeight: 700, fontSize: '0.95rem', mb: 0.4 }}>
                          {t('listing.errors.currencyLockedTitle')}
                        </AlertTitle>
                        <Typography variant="body2" sx={{ color: '#0277bd', lineHeight: 1.7, fontSize: '0.85rem' }}>
                          {t('listing.errors.currencyChangeRequiresNewListing')}
                        </Typography>
                      </Box>
                    </Box>
                  </Alert>
                </Grid>
              )}

              {/* Alerte incompatibilité devise/localisation en mode édition */}
              {errors.country_code && isEditMode && (
                <Grid item xs={12} ref={currencyErrorAlertRef}>
                  <Alert
                    severity="error"
                    icon={false}
                    sx={{
                      border: '2px solid #e53935',
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #ffebee 0%, #fff5f5 100%)',
                      boxShadow: '0 4px 20px rgba(211, 47, 47, 0.2)',
                      p: 2.5,
                      '& .MuiAlert-message': { width: '100%' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <ErrorIcon sx={{ color: '#c62828', fontSize: 32, mt: 0.3, flexShrink: 0 }} />
                      <Box>
                        <AlertTitle sx={{ color: '#c62828', fontWeight: 700, fontSize: '1rem', mb: 0.5, letterSpacing: 0.3 }}>
                          {t('listing.errors.currencyMismatchTitle')}
                        </AlertTitle>
                        <Typography variant="body2" sx={{ color: '#b71c1c', fontWeight: 500, lineHeight: 1.7 }}>
                          {errors.country_code}
                        </Typography>
                      </Box>
                    </Box>
                  </Alert>
                </Grid>
              )}
              
              {/* ─── Mode COVOITURAGE : Carte en haut ─── */}
              {formData.offer_type === 'ride_share' && (
                <Grid item xs={12}>
                   <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                      {t('listing.address.rideShareMapTitle', 'Carte du trajet')}
                   </Typography>
                   <Alert severity="info" sx={{ mb: 2 }}>
                      {t('listing.address.rideShareMapInstructions', 'Déplacez les marqueurs pour définir le départ et l\'arrivée.')}
                      <br />
                      {(formData.latitude && formData.longitude) && (
                         <em>{t('listing.map.departure', 'Départ')}: {Number(formData.latitude).toFixed(4)}, {Number(formData.longitude).toFixed(4)}</em>
                      )}
                      {(bookingSettings.destinationLat && bookingSettings.destinationLng) && (
                         <><br /><em>{t('listing.map.arrival', 'Arrivée')}: {Number(bookingSettings.destinationLat).toFixed(4)}, {Number(bookingSettings.destinationLng).toFixed(4)}</em></>
                      )}
                   </Alert>
                   <RideShareMap 
                      startPosition={formData.latitude && formData.longitude ? [formData.latitude, formData.longitude] : null}
                      endPosition={bookingSettings.destinationLat && bookingSettings.destinationLng ? [bookingSettings.destinationLat, bookingSettings.destinationLng] : null}
                      height={400}
                      onStartChange={(position) => {
                        console.log("[CreateListing] Start Position updated from RideShareMap:", position);
                        setFormData(prev => ({
                          ...prev,
                          latitude: position.latitude,
                          longitude: position.longitude
                        }));
                        
                        geocodingService.reverseGeocodeWithFallback(position.latitude, position.longitude)
                          .then(addressResult => {
                            setFormData(current => ({
                              ...current,
                              ...addressResult,
                              place_id: addressResult.place_id || current.place_id
                            }));
                          })
                          .catch(err => console.error('Reverse geocode error:', err));
                      }}
                      onEndChange={(position) => {
                        console.log("[CreateListing] End Position updated from RideShareMap:", position);
                        setBookingSettings(prev => ({
                          ...prev,
                          destinationLat: position.latitude,
                          destinationLng: position.longitude
                        }));
                        geocodingService.reverseGeocodeWithFallback(position.latitude, position.longitude)
                          .then(addressResult => {
                            if (addressResult.address) {
                              setBookingSettings(prev => ({ ...prev, destinationAddress: addressResult.address }));
                            }
                          })
                          .catch(() => {});
                      }}
                    />
                </Grid>
              )}

              {/* ─── Mode STANDARD : Carte en haut ─── */}
              {formData.offer_type !== 'ride_share' && (
                <Grid item xs={12}>
                   <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                      {t('listing.address.specifyLocationTitle')}
                   </Typography>
                   
                   <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>{t('common.info')}:</strong> {t('listing.address.dragMarkerToAdjust')}
                      <br />
                      {(formData.latitude && formData.longitude) && (
                        <em>{t('listing.address.coordinates')}: {Number(formData.latitude).toFixed(4)}, {Number(formData.longitude).toFixed(4)}</em>
                      )}
                   </Alert>

                   <ListingMap 
                      initialPosition={
                        (formData.latitude && formData.longitude && 
                         !isNaN(Number(formData.latitude)) && 
                         !isNaN(Number(formData.longitude)))
                        ? [Number(formData.latitude), Number(formData.longitude)] 
                        : [48.8566, 2.3522]
                      }
                      height={400}
                      onPositionChange={(position) => {
                        console.log("[CreateListing] Position mise à jour depuis ListingMap (Standard):", position);
                        setFormData(prev => {
                          const updatedData = {
                            ...prev,
                            latitude: position.latitude,
                            longitude: position.longitude
                          };
                          
                          // Géocodage inversé
                          geocodingService.reverseGeocodeWithFallback(position.latitude, position.longitude)
                            .then(addressResult => {
                              setFormData(current => ({
                                ...current,
                                ...addressResult,
                                place_id: addressResult.place_id || current.place_id
                              }));
                            })
                            .catch(error => console.error('Erreur géocodage:', error));
                          
                          return updatedData;
                        });
                      }}
                    />
                    
                   <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {t('listing.address.specifyLocationDescription')}
                   </Typography>
                </Grid>
              )}

              {/* Adresse de départ / principale */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={formData.offer_type === 'ride_share' ? t('listing.address.departureLabel', 'Adresse de départ') : t('listing.form.address')}
                  value={formData.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  error={!!errors.address}
                  helperText={errors.address || t('listing.address.helper.autoUpdate')}
                  inputRef={el => { fieldRefs.current.address = el; }}
                  required
                  multiline
                  rows={2}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(98, 0, 238, 0.1)'
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(98, 0, 238, 0.2)',
                        borderColor: '#6200ee'
                      }
                    }
                  }}
                />
              </Grid>

              {/* Adresse d'arrivée (seulement covoiturage) */}
              {formData.offer_type === 'ride_share' && (
                <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('listing.address.destinationLabel', 'Adresse d\'arrivée')}
                      value={bookingSettings.destinationAddress || ''}
                      onChange={(e) => {
                          const val = e.target.value;
                          setBookingSettings(prev => ({ ...prev, destinationAddress: val }));
                          // Effacer l'erreur si le champ est rempli
                          if (val && val.trim()) {
                            setErrors(prev => {
                                const newErr = { ...prev };
                                delete newErr.destinationAddress;
                                return newErr;
                            });
                          }
                      }}
                      error={!!errors.destinationAddress}
                      helperText={errors.destinationAddress}
                      required
                      multiline
                      rows={2}
                      variant="outlined"
                      inputRef={el => { fieldRefs.current.destinationAddress = el; }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                        }
                      }}
                    />
                </Grid>
              )}



              {/* Champs pour les coordonnées GPS (Réorganisés en bas) */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                 <Accordion variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                       <Typography variant="caption" color="text.secondary">
                          {t('listing.address.advancedCoordinates', 'Coordonnées GPS avancées')}
                       </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                       <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Latitude"
                              value={formData.latitude || ''}
                              onChange={(e) => {
                                const newLat = parseFloat(e.target.value);
                                handleInputChange('latitude', newLat);
                                if (formData.longitude) {
                                  // Trigger reverse geocoding
                                  geocodingService.reverseGeocodeWithFallback(newLat, formData.longitude)
                                    .then(res => setFormData(c => ({
                                      ...c, 
                                      ...res,
                                      // Ensure place_id is preserved if not returned by reverse geocoding
                                      place_id: res.place_id || c.place_id
                                    })))
                                    .catch(console.error);
                                }
                              }}
                              type="number"
                              InputProps={{ inputProps: { step: "0.000001" } }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Longitude"
                              value={formData.longitude || ''}
                              onChange={(e) => {
                                const newLng = parseFloat(e.target.value);
                                handleInputChange('longitude', newLng);
                                if (formData.latitude) {
                                  geocodingService.reverseGeocodeWithFallback(formData.latitude, newLng)
                                    .then(res => setFormData(c => ({
                                      ...c, 
                                      ...res,
                                      // Ensure place_id is preserved if not returned by reverse geocoding
                                      place_id: res.place_id || c.place_id
                                    })))
                                    .catch(console.error);
                                }
                              }}
                              type="number"
                              InputProps={{ inputProps: { step: "0.000001" } }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                             <Button
                                startIcon={<MyLocation />}
                                size="small"
                                onClick={() => {
                                  if (navigator.geolocation) {
                                    navigator.geolocation.getCurrentPosition(
                                      (position) => {
                                        const lat = position.coords.latitude;
                                        const lng = position.coords.longitude;
                                        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
                                        geocodingService.reverseGeocodeWithFallback(lat, lng)
                                          .then(res => {
                                            setFormData(c => ({
                                              ...c,
                                              ...res,
                                              place_id: res.place_id || c.place_id
                                            }));

                                            if (res.isFallback) {
                                              showNotification(
                                                t('listing.address.reverseGeocodingNoAddress'),
                                                'warning'
                                              );
                                            }
                                          })
                                          .catch(error => {
                                            console.error('Erreur géocodage inversé:', error);
                                            setFormData(c => ({
                                              ...c,
                                              address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
                                            }));
                                            showNotification(
                                              t('listing.address.reverseGeocodingNoAddress'),
                                              'warning'
                                            );
                                          });
                                      },
                                      (error) => {
                                        console.error('Erreur de géolocalisation:', error);
                                        showNotification(
                                          t('listing.address.geolocationPermissionDenied'), 
                                          'error'
                                        );
                                      }
                                    );
                                  }
                                }}
                              >
                                {t('listing.address.useMyLocation', 'Utiliser ma position actuelle')}
                              </Button>
                          </Grid>
                       </Grid>
                    </AccordionDetails>
                 </Accordion>
              </Grid>

            </Grid>
          </AccordionDetails>
        </Accordion>

        </>}

        {/* ─── Étape 2 : Réservation ─── */}
        {wizardStep === 2 && <>
        {/* Bannière preset catégorie */}
        {presetAppliedBanner && (
              <Alert severity="success"
            icon={<AutoFixHighIcon fontSize="inherit" />}
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setPresetAppliedBanner(null)}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                OK
              </Button>
            }
          >
            {t('listing:presetBanner.applied', {
              categoryName: presetAppliedBanner.categoryName
            })}
          </Alert>
        )}
        <BookingSettingsStep
          formData={formData}
          setFormData={setFormData}
          bookingSettings={bookingSettings}
          setBookingSettings={setBookingSettings}
          externalFieldErrors={bookingSettingsFieldErrors}
          externalGlobalErrors={bookingSettingsGlobalErrors}
        />
        </>}

        {/* ─── Étape 3 : Tarification + Disponibilités ─── */}
        {wizardStep === 3 && <>
        {/* Section Images */}
        <Accordion 
          expanded={expandedSections.images} 
          onChange={handleAccordionChange('images')}
          sx={{ 
            mb: 3,
            border: `2px solid ${getSectionColors('images').borderColor}`,
            backgroundColor: getSectionColors('images').backgroundColor,
            borderRadius: '12px !important',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: expandedSections.images ? 'scale(1.02)' : 'scale(1)',
            boxShadow: expandedSections.images ? 
              '0 8px 32px rgba(3, 218, 196, 0.25)' : 
              '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.01)',
              boxShadow: '0 12px 40px rgba(98, 0, 238, 0.3)',
              borderColor: getSectionColors('images').hoverBg.includes('primary') ? '#6200ee' : '#03dac4'
            },
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ 
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              transform: expandedSections.images ? 'rotate(180deg)' : 'rotate(0deg)'
            }} />}
            aria-controls="images-content"
            id="images-header"
            sx={{
              background: getSectionColors('images').hoverBg,
              color: getSectionColors('images').headerColor,
              minHeight: '64px !important',
              '&.Mui-expanded': {
                minHeight: '64px !important'
              },
              '& .MuiAccordionSummary-content': {
                margin: '16px 0 !important',
                '&.Mui-expanded': {
                  margin: '16px 0 !important'
                }
              },
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                background: getSectionColors('images').hoverBg,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 20px rgba(3, 218, 196, 0.3)'
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center">
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: getSectionStatus('images') ? '#ffffff' : 'rgba(255,255,255,0.5)',
                  mr: 2,
                  animation: getSectionStatus('images') ? 'glow 2s infinite' : 'none',
                  '@keyframes glow': {
                    '0%, 100%': { boxShadow: '0 0 5px rgba(255,255,255,0.5)' },
                    '50%': { boxShadow: '0 0 15px rgba(255,255,255,1)' }
                  }
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('listing.form.sections.images') || 'Images'}
                </Typography>
              </Box>
              {getSectionIcon('images')}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            p: 3,
            backgroundColor: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('listing.form.imagesDescription') || 'Ajoutez des images pour rendre votre annonce plus attractive'}
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  multiple
                  type="file"
                  onChange={handleImageUpload}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                  >
                    {t('listing.form.addImages')}
                  </Button>
                </label>
                
                {formData.images.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
                    {formData.images.map((image, index) => (
                      <Box key={index} sx={{ position: 'relative' }}>
                        <Box
                          sx={{
                            width: 100,
                            height: 100,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: index === 0 ? '3px solid #03dac4' : '2px solid transparent',
                            boxShadow: index === 0 ? '0 0 0 2px rgba(3,218,196,0.3)' : 'none',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={image.preview || image.url || image}
                            alt={`Preview ${index + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                          {index === 0 && (
                            <Box sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              bgcolor: 'rgba(3, 218, 196, 0.9)',
                              color: '#fff',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              textAlign: 'center',
                              py: 0.25,
                              letterSpacing: 0.3
                            }}>
                              {t('listing.form.mainPhoto', 'Photo principale')}
                            </Box>
                          )}
                        </Box>

                        {/* Boutons d'action */}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'center' }}>
                          {/* Définir comme principale (affiché sur toutes sauf la principale) */}
                          {index !== 0 && (
                            <IconButton
                              size="small"
                              onClick={() => handleSetMainImage(index)}
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: 'rgba(255,255,255,0.9)',
                                border: '1px solid #e0e0e0',
                                '&:hover': { backgroundColor: '#fff', borderColor: '#03dac4' }
                              }}
                              title={t('listing.form.setAsMain', 'Définir comme photo principale')}
                            >
                              <StarBorderIcon sx={{ fontSize: 14, color: '#03dac4' }} />
                            </IconButton>
                          )}
                          {index === 0 && (
                            <IconButton
                              size="small"
                              disabled
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: 'rgba(3, 218, 196, 0.1)',
                                border: '1px solid #03dac4',
                              }}
                              title={t('listing.form.isMainPhoto', 'Photo principale')}
                            >
                              <StarIcon sx={{ fontSize: 14, color: '#03dac4' }} />
                            </IconButton>
                          )}
                          {/* Supprimer */}
                          <IconButton
                            size="small"
                            onClick={() => removeImage(index)}
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              border: '1px solid #e0e0e0',
                              '&:hover': { backgroundColor: 'error.main', borderColor: 'error.main', color: '#fff' }
                            }}
                            title={t('listing.form.removeImage', 'Supprimer')}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Section Tarification */}
        <Accordion 
          expanded={expandedSections.pricing} 
          onChange={handleAccordionChange('pricing')}
          sx={{ 
            mb: 2,
            border: `2px solid ${getSectionColors('pricing').borderColor}`,
            backgroundColor: getSectionColors('pricing').backgroundColor,
            borderRadius: '12px !important',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: expandedSections.pricing ? 'scale(1.02)' : 'scale(1)',
            boxShadow: expandedSections.pricing ? 
              '0 8px 32px rgba(98, 0, 238, 0.25)' : 
              '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.01)',
              boxShadow: '0 12px 40px rgba(3, 218, 196, 0.3)',
              borderColor: getSectionColors('pricing').hoverBg.includes('primary') ? '#6200ee' : '#03dac4'
            },
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ 
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              transform: expandedSections.pricing ? 'rotate(180deg)' : 'rotate(0deg)'
            }} />}
            aria-controls="pricing-content"
            id="pricing-header"
            sx={{
              background: getSectionColors('pricing').headerBg,
              color: getSectionColors('pricing').headerColor,
              minHeight: '64px !important',
              '&.Mui-expanded': {
                minHeight: '64px !important'
              },
              '& .MuiAccordionSummary-content': {
                margin: '16px 0 !important',
                '&.Mui-expanded': {
                  margin: '16px 0 !important'
                }
              },
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                background: getSectionColors('pricing').hoverBg,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 20px rgba(98, 0, 238, 0.3)'
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center">
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: getSectionStatus('pricing') ? '#ffffff' : 'rgba(255,255,255,0.5)',
                  mr: 2,
                  animation: getSectionStatus('pricing') ? 'glow 2s infinite' : 'none',
                  '@keyframes glow': {
                    '0%, 100%': { boxShadow: '0 0 5px rgba(255,255,255,0.5)' },
                    '50%': { boxShadow: '0 0 15px rgba(255,255,255,1)' }
                  }
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('listing.form.sections.pricing') || 'Tarification'}
                </Typography>
              </Box>
              {getSectionIcon('pricing')}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            p: 3,
            backgroundColor: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('listing.form.pricingDescription') || 'Définissez au moins un prix pour votre annonce'}
                </Typography>
              </Grid>

              {/* Alerte pour afficher quel champ de prix est attendu */}
              {bookingSettings?.unit && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ fontSize: '0.9rem' }}>
                    {t('listing:pricing.unitDetected', {
                      unit: t(`listing:pricing.units.${bookingSettings.unit}`, bookingSettings.unit)
                    })}
                  </Alert>
                </Grid>
              )}
              
              {/* Sélecteur de devise pour l'annonce - Lecture seule, automatiquement défini par le pays */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <TextField
                    key={`currency-${currencyUpdateKey}`}
                    fullWidth
                    label={t('listing.form.currency')}
                    value={getCurrencyDisplayName()}
                    disabled={true}
                    sx={{ 
                      '& .Mui-disabled': { 
                        color: 'text.primary',
                        '-webkit-text-fill-color': 'unset'
                      }
                    }}
                    helperText={t('listing.form.currencyAutoSet')}
                  />
                </FormControl>
              </Grid>
              
              {/* Prix par heure - Visible si unité = heure */}
              {bookingSettings?.unit === 'hour' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    key={`priceHourly-${currencyUpdateKey}`}
                    fullWidth
                    type="number"
                    label={t('listing.form.priceHourly', { currency: getSelectedCurrencySymbol() })}
                    value={formData.priceHourly}
                    onChange={e => handleInputChange('priceHourly', e.target.value)}
                    error={!!errors.priceHourly}
                    helperText={errors.priceHourly}
                    inputRef={el => { fieldRefs.current.priceHourly = el; }}
                    inputProps={{
                      min: 0.01,
                      max: 999999999999999.99,
                      step: 0.01
                    }}
                  />
                </Grid>
              )}

              {/* Prix par créneau - Visible si unité = slot */}
              {bookingSettings?.unit === 'slot' && (
                <Grid item xs={12} md={6}>
                  {(() => {
                    const pricingMode = bookingSettings?.pricing_mode || 'per_unit';
                    const currencySym = getSelectedCurrencySymbol();

                    const translatedGenericLabel = t('listing.form.pricePerSlot', { currency: currencySym, defaultValue: '' });

                    const label =
                      pricingMode === 'per_seat'
                        ? t('listing:pricingModes.labels.perSeat', { symbol: currencySym })
                        : pricingMode === 'per_unit_per_seat'
                        ? t('listing:pricingModes.labels.perUnitPerSeat', { symbol: currencySym })
                        : pricingMode === 'flat'
                        ? t('listing:pricingModes.labels.flat', { symbol: currencySym })
                        : t('listing:pricingModes.labels.default', { symbol: currencySym });

                    const hint =
                      pricingMode === 'per_seat'
                        ? t('listing:pricingModes.hints.perSeat')
                        : pricingMode === 'per_unit_per_seat'
                        ? t('listing:pricingModes.hints.perUnitPerSeat')
                        : pricingMode === 'flat'
                        ? t('listing:pricingModes.hints.flat')
                        : t('listing:pricingModes.hints.default');

                    return (
                      <TextField
                        key={`pricePerSlot-${currencyUpdateKey}`}
                        fullWidth
                        type="number"
                        label={(pricingMode === 'per_unit' && translatedGenericLabel) ? translatedGenericLabel : label}
                        value={formData.pricePerSlot}
                        onChange={e => handleInputChange('pricePerSlot', e.target.value)}
                        error={!!errors.pricePerSlot}
                        helperText={errors.pricePerSlot || hint}
                        inputRef={el => { fieldRefs.current.pricePerSlot = el; }}
                        inputProps={{
                          min: 0.01,
                          max: 999999999999999.99,
                          step: 0.01
                        }}
                      />
                    );
                  })()}
                </Grid>
              )}
              
              {/* Champs de prix alignés vers la gauche - Visibles si unité = jour */}
              {bookingSettings?.unit === 'day' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      key={`priceDaily-${currencyUpdateKey}`}
                      fullWidth
                      type="number"
                      label={t('listing.form.priceDaily', { currency: getSelectedCurrencySymbol() })}
                      value={formData.priceDaily}
                      onChange={e => handleInputChange('priceDaily', e.target.value)}
                      error={!!errors.priceDaily}
                      helperText={errors.priceDaily}
                      inputRef={el => { fieldRefs.current.priceDaily = el; }}
                      inputProps={{
                        min: 0.01,
                        max: 999999999999999.99,
                        step: 0.01
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      key={`priceWeekly-${currencyUpdateKey}`}
                      fullWidth
                      type="number"
                      label={t('listing.form.priceWeekly', { currency: getSelectedCurrencySymbol() })}
                      value={formData.priceWeekly}
                      onChange={e => handleInputChange('priceWeekly', e.target.value)}
                      error={!!errors.priceWeekly}
                      helperText={errors.priceWeekly}
                      inputRef={el => { fieldRefs.current.priceWeekly = el; }}
                      inputProps={{
                        min: 0.01,
                        max: 999999999999999.99,
                        step: 0.01
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      key={`priceMonthly-${currencyUpdateKey}`}
                      fullWidth
                      type="number"
                      label={t('listing.form.priceMonthly', { currency: getSelectedCurrencySymbol() })}
                      value={formData.priceMonthly}
                      onChange={e => handleInputChange('priceMonthly', e.target.value)}
                      error={!!errors.priceMonthly}
                      helperText={errors.priceMonthly}
                      inputRef={el => { fieldRefs.current.priceMonthly = el; }}
                      inputProps={{
                        min: 0.01,
                        max: 999999999999999.99,
                        step: 0.01
                      }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  key={`depositAmount-${currencyUpdateKey}`}
                  fullWidth
                  type="number"
                  label={t('listing.form.depositAmount', { currency: getSelectedCurrencySymbol() })}
                  value={formData.depositAmount}
                  onChange={e => handleInputChange('depositAmount', e.target.value)}
                  error={!!errors.depositAmount}
                  helperText={errors.depositAmount}
                  required
                  inputRef={el => { fieldRefs.current.depositAmount = el; }}
                  inputProps={{
                    min: 0,
                    max: 999999999999999.99,
                    step: 0.01
                  }}
                />
              </Grid>

              {/* Horaires début / fin — pour tous les listings en mode date_range */}
              {formData.booking_mode === 'date_range' && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {formData.offer_type === 'property'
                          ? `🏠 ${t('listing:property.section')}`
                          : `⏰ ${t('listing:dateRange.section')}`
                        }
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label={formData.offer_type === 'property' ? t('listing:property.checkinTime') : t('listing:dateRange.startTime')}
                      value={bookingSettings.checkin_time || '14:00'}
                      onChange={e => setBookingSettings(prev => ({ ...prev, checkin_time: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      helperText={formData.offer_type === 'property' ? t('listing:property.checkinTimeHint') : t('listing:dateRange.startTimeHint')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label={formData.offer_type === 'property' ? t('listing:property.checkoutTime') : t('listing:dateRange.endTime')}
                      value={bookingSettings.checkout_time || '12:00'}
                      onChange={e => setBookingSettings(prev => ({ ...prev, checkout_time: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      helperText={formData.offer_type === 'property' ? t('listing:property.checkoutTimeHint') : t('listing:dateRange.endTimeHint')}
                    />
                  </Grid>

                  {/* Aperçu visuel des horaires */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#fff', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              {formData.offer_type === 'property'
                                ? t('listing:property.checkinLabel')
                                : t('listing:dateRange.startLabel')
                              }
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#6200ee', fontWeight: 'bold', mt: 1 }}>
                              {bookingSettings.checkin_time || '14:00'}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#fff', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              {formData.offer_type === 'property'
                                ? t('listing:property.checkoutLabel')
                                : t('listing:dateRange.endLabel')
                              }
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#03dac4', fontWeight: 'bold', mt: 1 }}>
                              {bookingSettings.checkout_time || '12:00'}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </>
              )}
              
              {/* Affichage des erreurs de tarification */}
              {errors.pricing && (
                <Grid item xs={12}>
                  <Alert severity="error">{errors.pricing}</Alert>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
        
        {/* Section Disponibilités */}
        <Accordion 
          expanded={isAvailabilityAccordionExpanded}
          sx={{ 
            mb: 2,
            border: `2px solid ${getSectionColors('availability')?.borderColor || '#03dac4'}`,
            backgroundColor: getSectionColors('availability')?.backgroundColor || 'rgba(3, 218, 196, 0.05)',
            borderRadius: '12px !important',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isAvailabilityAccordionExpanded ? 'scale(1.02)' : 'scale(1)',
            boxShadow: isAvailabilityAccordionExpanded ? 
              '0 8px 32px rgba(3, 218, 196, 0.25)' : 
              '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.01)',
              boxShadow: '0 12px 40px rgba(3, 218, 196, 0.3)',
              borderColor: '#03dac4'
            },
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ 
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              transform: isAvailabilityAccordionExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }} />}
            aria-controls="availability-content"
            id="availability-header"
            sx={{
              background: getSectionColors('availability').headerBg,
              color: getSectionColors('availability').headerColor,
              minHeight: '64px !important',
              '&.Mui-expanded': {
                minHeight: '64px !important'
              },
              '& .MuiAccordionSummary-content': {
                margin: '16px 0 !important',
                '&.Mui-expanded': {
                  margin: '16px 0 !important'
                }
              },
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                background: getSectionColors('availability').hoverBg,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 20px rgba(3, 218, 196, 0.3)'
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center">
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: getSectionStatus('availability') ? '#ffffff' : 'rgba(255,255,255,0.5)',
                  mr: 2,
                  animation: getSectionStatus('availability') ? 'glow 2s infinite' : 'none',
                  '@keyframes glow': {
                    '0%, 100%': { boxShadow: '0 0 5px rgba(255,255,255,0.5)' },
                    '50%': { boxShadow: '0 0 15px rgba(255,255,255,1)' }
                  }
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('availabilityManager:manageTitle', 'Gérer les disponibilités')}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Button 
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    console.log('🔄 Simple refresh - Accordéon ouvert forcé');
                    setExpandedSections(prev => ({ ...prev, availability: true }));
                    showNotification(t('common.interfaceRefreshed'), 'info');
                  }}
                  startIcon={<RefreshIcon />}
                  sx={{ 
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  {t('common.refresh')}
                </Button>
                {getSectionIcon('availability')}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            p: 3,
            backgroundColor: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Box>
              <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
                {/* Indicateur d'état en haut à droite */}
                <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 1000, backgroundColor: 'rgba(255,255,255,0.8)', padding: 1, borderRadius: 1 }}>
                  <Typography variant="caption">
                    Périodes: {availabilityPeriods.length} | {t('listing:debug.loading')}: {availabilityLoading ? t('common.yes') : t('common.no')} | {t('listing:debug.mode')}: {isEditMode ? t('listing.status.active') : t('common.create')}
                  </Typography>
                </Box>
                              
                
                {/* Ajouter des logs pour déboguer les périodes */}
                {console.log('🔍 Rendering NewAvailabilityManager with:', {
                  availabilityPeriods,
                  isEditMode,
                  id,
                  useLocalOnly: !isEditMode
                })}
                
                <NewAvailabilityManager
                  listingId={isEditMode ? id : null}
                  availabilityPeriods={availabilityPeriods}
                  excludedDateRanges={deletedPeriodDateRanges}
                  bookingMode={formData.booking_mode}
                  capacityDefault={formData.capacity_default}
                  onAvailabilityPeriodsChange={setAvailabilityPeriods}
                  onSelectPeriod={(period) => {
                    setSelectedPeriod(period);
                    handleOpenPeriodModal('edit', period);
                  }}
                  onAddPeriod={async (periodData) => {
                    // Verrouiller l'accordéon ouvert IMMÉDIATEMENT (avant tout await)
                    keepAvailabilityExpandedRef.current = true;
                    setExpandedSections(prev => ({ ...prev, availability: true }));
                    try {
                      console.log('onAddPeriod appelé avec:', periodData);
                      await handleSavePeriod(periodData);
                    } catch (error) {
                      console.error('Erreur lors de la création de la période:', error);
                    }
                  }}
                  onUpdatePeriod={(periodData) => {
                    setSelectedPeriod(periodData);
                    handleOpenPeriodModal('edit', periodData);
                  }}
                  onDeletePeriod={handleDeletePeriod}
                />
              </Paper>
              
              <PeriodFormModal
                open={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                onSave={handleSavePeriod}
                onDelete={selectedPeriod ? () => handleDeletePeriod(selectedPeriod.id) : null}
                initialData={selectedPeriod}
                mode={availabilityFormMode}
                isSubmitting={false}
                availabilityPeriods={availabilityPeriods}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Section Récapitulatif des prix */}

        </>}

        {/* ─── Navigation Wizard ─────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, pt: 3, borderTop: '1px solid #f0f0f0' }}>
          {/* Bouton retour / annuler */}
          {wizardStep === 0 ? (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ borderColor: '#6200ee', color: '#6200ee', borderRadius: 2, textTransform: 'none' }}
            >
              {t('common.cancel', { defaultValue: 'Annuler' })}
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleWizardBack}
              sx={{ borderColor: '#6200ee', color: '#6200ee', borderRadius: 2, textTransform: 'none' }}
            >
              {t('common.back', { defaultValue: 'Retour' })}
            </Button>
          )}

          {/* Indicateur d'étape */}
          <Typography variant="caption" color="text.secondary">
            {wizardStep + 1} / {WIZARD_STEPS.length} — {WIZARD_STEPS[wizardStep]?.label}
          </Typography>

          {/* Bouton suivant ou soumettre */}
          {wizardStep < WIZARD_STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleWizardNext}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white', borderRadius: 2, textTransform: 'none',
                px: 3, fontWeight: 600,
                '&:hover': { background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)' }
              }}
            >
              {t('common.next', { defaultValue: 'Suivant' })} →
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !!errors.country_code}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white', borderRadius: 2, textTransform: 'none',
                px: 4, py: 1.5, fontSize: '1rem', fontWeight: 600,
                boxShadow: '0 4px 20px rgba(98,0,238,0.3)',
                '&:hover': { background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)', transform: 'translateY(-2px)' },
                '&:disabled': { background: 'linear-gradient(45deg,#9e9e9e 30%,#bdbdbd 90%)', color: '#fff' }
              }}
            >
              {loading
                ? (isEditMode ? t('common.updating') : t('common.creating'))
                : (isEditMode ? t('listing.edit.submit') : t('listing.create.submit'))}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateListingForm;